import * as maplibregl from "maplibre-gl";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { LngLat } from "../types";
import type { RailingStyle } from "../types";
import type {
  DecorGround,
  DecorItem,
  DecorModelBounds,
} from "../../core/decor";
import {
  DECOR_GROUND_MAX_HEIGHT,
  DecorGroundIndex,
  decorPose,
} from "../../core/decor";
import type { DecorModel } from "../../core/decorModels";
import { isDecorModelMovable, isFixedProceduralModel } from "../../core/decorModels";
import {
  buildStairsOpening,
  stairsOpeningProfile,
} from "./proceduralStairs";
import {
  buildJapanesePine,
  buildOlive,
  buildPottedOlive,
  buildSakura,
} from "./proceduralTrees";
import {
  buildFirstAidPost,
  buildInfoDesk,
  buildLanternGarland,
  buildYatai,
} from "./proceduralFixtures";
import { buildFence } from "./proceduralFence";
import { buildRockMassif } from "./proceduralRockMassif";
import { buildFairytaleCastle } from "./proceduralCastle";
import {
  buildExcursionCoach,
  buildSteamLocomotive,
  buildSteamTender,
} from "./proceduralTrain";
import {
  buildPicnicTables,
  buildSeating,
  buildStage,
} from "./proceduralStage";
import {
  buildFreeFlight,
  defaultFlightRise,
  freeFlightProfile,
  isFreeFlight,
} from "./proceduralFlights";
import type { DecorFlight } from "../../core/decorFlights";
import { DEFAULT_DECOR_SMOKE, SMOKE_SOURCE_NODE, SmokePlume } from "./decorSmoke";
import { flightAt } from "../../core/decorFlights";
import { buildDugArea, buildPitDecals, edgeOutsideGaps, pathGaps, pitWaterSurface } from "./dugArea";
import type { GroundPoint } from "./dugArea";
import type { PitVolume } from "./dugArea";
import { WaterRipples } from "./waterRipples";
import { SurfaceScatter } from "./surfaceScatter";
import type { ScatterPreset } from "./surfaceScatterSampling";
import { surfaceTexture3d } from "./surfaceTexture3d";
import { surface as flatSurface } from "./dugArea";
import type { ShapeSurface, SurfaceMaterial } from "../types";
import { buildRailings, outlineEdges } from "./railing";
import type { RailingEdge } from "./railing";
import { pointInPolygon } from "../../core/pathfinding";
import { coatColors, prepareCoat } from "./decorCoat";
import { collectColorSlots, colorsSignature, recolorMaterial, withShader } from "./decorColors";
import type { DecorColorSlot } from "./decorColors";
import { prepareWind, windUniforms } from "./decorWind";
import type { RoadFeature } from "./roadClearings";
import { waterDetailShare, waterUniforms } from "./waterWaves";
import { RenderStatsWindow } from "../../core/renderStats";
import { disposeMerged, mergeStaticRoots } from "./staticMerge";
import { isVatMesh, startVatBake, vatMaterial } from "./vatCrowd";
import { sceneMemoryBytes } from "./sceneMemory";
import type { VatBake, VatBaker, VatClip, VatMesh } from "./vatCrowd";
import type { FrameTimings } from "../../core/renderStats";

export const DECOR_LAYER_ID = "tokamap-decor";

/** Ce que coûte le décor, pour la fenêtre de diagnostic : temps par image et volumes dessinés. */
export interface DecorRenderStats {
  timings: FrameTimings;
  items: number;
  /** Objets revus à chaque image : en mouvement, animés ou en surbrillance. */
  active: number;
  /** Objets dessinés un par un, hors des lots instanciés. */
  standalone: number;
  batches: number;
  batchedInstances: number;
  /** Instances des lots effectivement dessinées : les plus petites à l'écran sont écartées. */
  visibleInstances: number;
  impostors: number;
  /** Touffes, cailloux et feuilles semés sur les sols, dessinés à la dernière image. */
  scattered: number;
  animated: number;
  drawCalls: number;
  triangles: number;
  batchSyncsPerSecond: number;
  batchSyncMs: number;
  /** Temps moyen par image de chaque étape du rendu du décor, en millisecondes. */
  phases: Record<string, number>;
}

const PHASE_PREFIX = "phase:";
/** Temps accordé par image au précalcul des poses d'un modèle animé (voir `startVatBake`). */
const VAT_BAKE_BUDGET_MS = 4;

interface DecorTemplate {
  root: THREE.Object3D;
  clips: THREE.AnimationClip[];
  skinned: boolean;
  bounds: DecorModelBounds;
  opening: boolean;
  instanceable: boolean;
  /** Bouge au vent (`prepareWind`) : la carte est redessinée tant qu'il est à l'écran. */
  windy?: boolean;
  colorSlots: ReturnType<typeof collectColorSlots>;
  /** Deux plans croisés à l'image de l'arbre, pour le dessiner de loin ; construits à la première occasion. */
  impostor?: THREE.Mesh | "pending";
  /** Ses poses précalculées (`startVatBake`), `null` quand il ne s'y prête pas. */
  vat?: VatBake | null | "pending";
  vatBaker?: VatBaker | null;
}

interface DecorInstance {
  modelId: string;
  root: THREE.Object3D;
  meshes: THREE.Mesh[];
  mixer: THREE.AnimationMixer | null;
  actions: Map<string, THREE.AnimationAction>;
  current: THREE.AnimationAction | null;
  highlight: DecorHighlightState;
  smoke: { source: THREE.Object3D; plume: SmokePlume } | null;
  /** Signature des couleurs appliquées (`colorsSignature`). */
  colors: string;
  /** Les matériaux recolorés qu'il utilise, par clé de `DecorLayer.recolored`. */
  recoloredKeys: string[];
  /** Temps d'animation propre à l'objet quand il est dessiné par poses précalculées. */
  vatTime: number;
}

type DecorHighlightState = "none" | "hovered" | "selected";

interface DecorBatch {
  mesh: THREE.InstancedMesh;
  capacity: number;
  chunk: string;
  /** Seuil de chaque instance, en mètres par pixel, du plus grand au plus petit (`visibleInstanceCount`). */
  thresholds: Float32Array;
  /** Gabarit qui a une version simplifiée : ses lots complets cèdent la place à celle-ci de loin. */
  impostorOf: string | null;
  impostor: boolean;
  windy: boolean;
}

/** L'élément sélectionné et celui survolé, teintés de `color` — voir `DecorLayer.setHighlight`. */
export interface DecorHighlight {
  selectedId: string | null;
  hoveredId: string | null;
  color: string;
}

/** Zone creusée dans le sol, voir `Shape.depth`. */
export interface DecorPit {
  ring: LngLat[];
  holes: LngLat[][];
  depth: number;
  floorColor?: string;
  floorSurface?: ShapeSurface;
  /** Teinte du fond (`rrggbb`), quand la matière la reprend. */
  floorTint?: string;
  /** Hauteur du bord, quand la zone est creusée dans le fond d'une autre ; `depth` se compte alors toujours depuis le sol. */
  rim?: number;
  /** Les zones creusées dans le fond de celle-ci. */
  floorHoles?: LngLat[][];
  railing?: RailingStyle;
  water?: boolean;
  decals?: DecorPitDecal[];
  /** Barrières des formes posées au fond. */
  railings?: DecorRailing[];
  /** Tracés des ouvertures du contour, où la barrière s'interrompt. */
  openings?: LngLat[][];
}

/** Barrière le long des contours d'une forme, voir `Shape.railing`. */
export interface DecorScatterArea {
  id: string;
  material: SurfaceMaterial;
  preset?: ScatterPreset;
  band?: number;
  ring: LngLat[];
  holes: LngLat[][];
  base: number;
  density: number;
  tint?: string;
  roadBounds?: [number, number, number, number];
}

/** Le toit d'un volume, couvert de la matière de la forme. */
export interface DecorSurfaceRoof {
  ring: LngLat[];
  holes: LngLat[][];
  top: number;
  surface: ShapeSurface;
  tint?: string;
}

export interface DecorRailing {
  lines: LngLat[][];
  /** Une ligne n'a pas de dernier côté. */
  closed: boolean;
  /** Hauteur du pied, en mètres par rapport au sol. */
  base: number;
  style: RailingStyle;
}

/** Une forme plate posée au fond d'une zone creusée. */
export interface DecorPitDecal {
  ring: LngLat[];
  holes: LngLat[][];
  closed: boolean;
  fillColor?: string;
  borderColor?: string;
  borderWidth: number;
  borderWidthUnit: "px" | "m";
  /** Tiret et blanc, en multiples de l'épaisseur de bordure. */
  borderDash?: [number, number];
  volume?: PitVolume;
}

const EARTH_CIRCUMFERENCE_M = 40_075_016.686;
/** Une bordure en pixels se recalcule à chaque palier de zoom. */
const DECAL_ZOOM_STEP = 0.5;
const DECAL_PITCH_STEP_DEG = 10;

export interface DecorLayerOptions {
  animated: boolean;
  models: Record<string, DecorModel>;
  onModelLoaded: () => void;
  resolveImageUrl?: (src: string) => string;
}

const WALK_SPEED_KMH = 4.5;
/** En deçà, un objet qui ne se déplace pas (marches d'escalator) reste figé : de loin, son animation ne se voit pas. */
const STATIC_ANIMATION_MIN_ZOOM = 19;
/** En deçà, les passants et véhicules s'arrêtent : ils ne font plus que quelques pixels. */
const MOVING_ANIMATION_MIN_ZOOM = 16.5;
/**
 * En deçà, un objet n'est pas dessiné : quelques pixels ne se lisent pas et brouillent la vue
 * d'ensemble. L'objet sélectionné ou survolé reste toujours visible.
 */
const MIN_DECOR_SCREEN_PX = 4;
/** Cadence des animations : la carte n'est redessinée pour elles qu'à ce rythme. */
const ANIMATION_FRAME_MS = 1000 / 30;
/** Le toit texturé d'un volume, juste au-dessus du sien pour ne pas s'y confondre. */
const ROOF_SURFACE_LIFT_M = 0.02;
/** Pendant un mouvement de caméra, les lots instanciés ne sont recomposés qu'à ce rythme. */
const BATCH_SYNC_WHILE_MOVING_MS = 250;
/** Côté d'un secteur de lots : le GPU écarte ceux hors de l'écran, une modification n'en refait qu'un. */
const BATCH_CHUNK_M = 300;
/** Temps accordé par image à la recomposition des lots ; le reste attend l'image suivante. */
const BATCH_SYNC_BUDGET_MS = 4;
/** Au-delà, un arbre ne fait plus qu'une trentaine de pixels : deux plans croisés le remplacent. */
const IMPOSTOR_METERS_PER_PIXEL = 0.25;
const IMPOSTOR_TEXTURE_PX = 128;
/** Temps sans changement de palier avant de refaire les bordures en pixels des zones creusées. */
const PIT_DECALS_SETTLE_MS = 200;
/** Un palier de zoom, en dixièmes de doublement : sous ce pas, la visibilité des objets isolés ne change pas. */
const LOD_STEPS_PER_DOUBLING = 10;
/** Au-delà, l'écart depuis l'image précédente n'est pas rattrapé : la carte est restée immobile entre-temps. */
const MAX_ANIMATION_STEP_S = 0.25;
const VIEW_MARGIN_PX = 80;
/** Au-delà de cette surface à l'écran, un objet que le clic ne touche pas n'est plus « frôlé ». */
const GRAZE_MAX_AREA_PX = 100 * 100;
const PREVIEW_OPACITY = 0.55;
const HIGHLIGHT_INTENSITY: Record<DecorHighlightState, number> = { none: 0, hovered: 0.25, selected: 0.5 };
/** Part de la teinte mêlée à la couleur d'un matériau sans éclairage : plus forte, faute d'émissif qui éclaircisse. */
const UNLIT_HIGHLIGHT_MIX: Record<DecorHighlightState, number> = { none: 0, hovered: 0.45, selected: 0.7 };

/** Couches three.js des passes d'une trémie — voir `renderOpenings`. */
const LAYER_PIT = 1;
const LAYER_DEPTH_RESET = 2;
const LAYER_MASK = 3;
const LAYER_MASK_OCCLUSION = 4;
const LAYER_GROUND_SEAL = 5;
/** Le sol que MapLibre dessine n'a pas de profondeur : sans lui, ce qui est sous terre se verrait au travers. */
const GROUND_SEAL_SIZE_M = 50_000;
/**
 * Hauteur de la surface qui retire de l'ouverture ce qui passe devant elle : au-dessus des dalles,
 * qui ne doivent pas la masquer, en dessous des murs, qui le doivent.
 */
const OPENING_OCCLUDER_HEIGHT = DECOR_GROUND_MAX_HEIGHT + 0.05;
/** Assez large pour qu'un regard rasant vers le bord de l'ouverture passe encore au-dessus des dalles. */
const OPENING_OCCLUDER_SKIRT_WIDTH = 50;
/** Bit du pochoir réservé aux ouvertures ; MapLibre n'emploie les bits hauts qu'au-delà de 127 tuiles découpées. */
const OPENING_STENCIL_BIT = 0x80;

const CONTACT_SHADOW_NODE = "ContactShadow";

/** Soleil des objets 3D : d'où vient la lumière, depuis le nord, et son inclinaison par rapport à la verticale. */
export interface DecorSun {
  azimuthDeg: number;
  angleDeg: number;
}
/** Débord de l'ombre au-delà de l'emprise de l'objet, pour que son dégradé ne s'arrête pas net à ses pieds. */
const CONTACT_SHADOW_MARGIN = 1.6;
/** `alphaMap` lit l'opacité dans le canal vert : un dégradé blanc sur fond noir, pas un noir transparent. */
function createContactShadowMaterial(): THREE.MeshBasicMaterial {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 64;
  const context = canvas.getContext("2d")!;
  context.fillStyle = "#000";
  context.fillRect(0, 0, 64, 64);
  const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, "#fff");
  gradient.addColorStop(0.55, "#999");
  gradient.addColorStop(1, "#000");
  context.fillStyle = gradient;
  context.fillRect(0, 0, 64, 64);
  return new THREE.MeshBasicMaterial({
    color: 0x000000,
    alphaMap: new THREE.CanvasTexture(canvas),
    transparent: true,
    opacity: 0.4,
    depthWrite: false,
  });
}

/**
 * L'arbre vu de face, rendu une fois dans une texture et posé sur deux plans croisés, dans le repère
 * de son gabarit : il prend la place exacte du modèle dans un lot. L'éclairage est celui de la scène
 * au moment du rendu ; le contexte WebGL étant celui de MapLibre, sa zone d'affichage est rendue intacte.
 */
function bakeImpostor(renderer: THREE.WebGLRenderer, template: DecorTemplate, lights: THREE.Light[]): THREE.Mesh {
  const { bounds } = template;
  const width = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, 0.1);
  const height = Math.max(bounds.height, 0.1);
  const target = new THREE.WebGLRenderTarget(IMPOSTOR_TEXTURE_PX, height > width * 1.5 ? IMPOSTOR_TEXTURE_PX * 2 : IMPOSTOR_TEXTURE_PX, {
    generateMipmaps: true,
    minFilter: THREE.LinearMipmapLinearFilter,
  });
  const scene = new THREE.Scene();
  const subject = template.root.clone(true);
  subject.traverse((node) => {
    if (node.name === CONTACT_SHADOW_NODE) node.visible = false;
  });
  scene.add(subject);
  for (const light of lights) scene.add(light.clone());
  const camera = new THREE.OrthographicCamera(-width / 2, width / 2, height, 0, 0.1, 1000);
  camera.position.set(0, 0, 500);
  camera.lookAt(0, 0, 0);

  const gl = renderer.getContext();
  const viewport = gl.getParameter(gl.VIEWPORT) as Int32Array;
  const framebuffer = gl.getParameter(gl.FRAMEBUFFER_BINDING) as WebGLFramebuffer | null;
  const clearColor = renderer.getClearColor(new THREE.Color());
  const clearAlpha = renderer.getClearAlpha();
  renderer.setRenderTarget(target);
  renderer.setClearColor(0x000000, 0);
  renderer.clear();
  renderer.render(scene, camera);
  renderer.setRenderTarget(null);
  renderer.setClearColor(clearColor, clearAlpha);
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.viewport(viewport[0]!, viewport[1]!, viewport[2]!, viewport[3]!);

  const plane = new THREE.PlaneGeometry(width, height).translate(0, height / 2, 0);
  const geometry = mergeGeometries([plane, plane.clone().rotateY(Math.PI / 2)]);
  const material = new THREE.MeshBasicMaterial({ map: target.texture, alphaTest: 0.4, side: THREE.DoubleSide });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = "Impostor";
  return mesh;
}

/** Tache d'ombre douce sous l'objet, dans l'espace de son gabarit : elle suit sa position, son cap et son échelle. */
function addContactShadow(root: THREE.Object3D, bounds: DecorModelBounds, material: THREE.Material): void {
  const width = (bounds.maxX - bounds.minX) * CONTACT_SHADOW_MARGIN;
  const depth = (bounds.maxY - bounds.minY) * CONTACT_SHADOW_MARGIN;
  if (width <= 0 || depth <= 0) return;
  const geometry = new THREE.PlaneGeometry(width, depth).rotateX(-Math.PI / 2);
  geometry.translate((bounds.minX + bounds.maxX) / 2, 0.02, -(bounds.minY + bounds.maxY) / 2);
  const shadow = new THREE.Mesh(geometry, material);
  shadow.name = CONTACT_SHADOW_NODE;
  shadow.renderOrder = -1;
  root.add(shadow);
}

/** La matrice monde d'un nœud recalculée depuis `root` seulement, sans descendre dans ses voisins. */
export function worldMatrixFromRoot(node: THREE.Object3D, root: THREE.Object3D): void {
  const chain: THREE.Object3D[] = [];
  for (let current: THREE.Object3D | null = node; current && current !== root; current = current.parent) chain.unshift(current);
  let parent = root.matrixWorld;
  for (const current of chain) {
    current.updateMatrix();
    current.matrixWorld.multiplyMatrices(parent, current.matrix);
    parent = current.matrixWorld;
  }
}

/** `matrixWorldAutoUpdate` à faux fige aussi la mise à jour forcée : un objet immobile ne se replacerait plus. */
export function refreshWorldMatrix(object: THREE.Object3D): void {
  const auto = object.matrixWorldAutoUpdate;
  object.matrixWorldAutoUpdate = true;
  object.updateMatrixWorld(true);
  object.matrixWorldAutoUpdate = auto;
}

/** Pendant un mouvement de caméra, les lots ne sont recomposés qu'à intervalle. */
export function shouldSyncBatches(state: { cameraMoving: boolean; now: number; lastSync: number }): boolean {
  return !state.cameraMoving || state.now - state.lastSync >= BATCH_SYNC_WHILE_MOVING_MS;
}

/** Le secteur de lots d'une position de la scène, en mètres. */
export function chunkKeyOf(x: number, z: number): string {
  return `${Math.floor(x / BATCH_CHUNK_M)}:${Math.floor(z / BATCH_CHUNK_M)}`;
}

export function chunkCenter(key: string): [number, number] {
  const [i, j] = key.split(":").map(Number) as [number, number];
  return [(i + 0.5) * BATCH_CHUNK_M, (j + 0.5) * BATCH_CHUNK_M];
}

export function lodStepOf(metersPerPixel: number): number {
  return Math.round(Math.log2(metersPerPixel) * LOD_STEPS_PER_DOUBLING);
}

/** Ce qui se lit d'un objet à l'écran : sa hauteur, ou sa plus petite largeur — une clôture se lit à sa hauteur. */
export function decorScreenSize(bounds: DecorModelBounds): number {
  return Math.max(bounds.height, Math.min(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY));
}

/**
 * Le nombre d'instances encore assez grandes à l'écran, dans un lot trié de la plus grande à la plus
 * petite (`thresholds`, en mètres par pixel décroissants) : il suffit de n'en dessiner que le début.
 */
export function visibleInstanceCount(thresholds: ArrayLike<number>, metersPerPixel: number): number {
  let low = 0;
  let high = thresholds.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (thresholds[middle]! >= metersPerPixel) low = middle + 1;
    else high = middle;
  }
  return low;
}

interface DecorFrame {
  elapsed: number | null;
  delta: number;
  metersPerPixel: number;
  movingAnimated: boolean;
  staticAnimated: boolean;
  onScreen: (position: LngLat) => boolean;
  animating: boolean;
  active: number;
  animated: number;
  /** Maillages des objets en mouvement, dessinés par des lots refaits à chaque image. */
  dynamicMeshes: THREE.Mesh[];
  /** Objets animés dessinés par poses précalculées, et où ils en sont. */
  vatInstances: { instance: DecorInstance; clip: VatClip; phase: number }[];
}

/** Posé à la surface d'une zone creusée en eau. */
export function isOnWaterSurface(position: LngLat, elevation: number, pits: DecorPit[]): boolean {
  return pits.some((pit) =>
    pit.water
    && Math.abs(elevation - pitWaterSurface(pit.depth, pit.rim)) < 0.01
    && pointInPolygon(position, pit.ring)
    && !pit.holes.some((hole) => pointInPolygon(position, hole)));
}

function isInPit(object: THREE.Object3D): boolean {
  for (let node: THREE.Object3D | null = object; node; node = node.parent) if (node.name.startsWith("Pit")) return true;
  return false;
}

/** Boîte englobante hors fosse : une trémie se pose par son ouverture, pas par le fond de sa fosse. */
function aboveGroundBox(object: THREE.Object3D): THREE.Box3 {
  object.updateMatrixWorld(true);
  const box = new THREE.Box3();
  object.traverse((node) => {
    if (node instanceof THREE.Mesh && !isInPit(node) && node.name !== CONTACT_SHADOW_NODE) box.expandByObject(node, true);
  });
  return box.isEmpty() ? new THREE.Box3().setFromObject(object) : box;
}

function isShown(object: THREE.Object3D): boolean {
  for (let node: THREE.Object3D | null = object; node; node = node.parent) if (!node.visible) return false;
  return true;
}

function meshesOf(root: THREE.Object3D): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];
  root.traverse((node) => {
    if (node instanceof THREE.Mesh) meshes.push(node);
  });
  return meshes;
}

function isInstanceable(root: THREE.Object3D, clips: THREE.AnimationClip[], skinned: boolean, opening: boolean): boolean {
  return clips.length === 0 && !skinned && !opening && !root.getObjectByName(SMOKE_SOURCE_NODE);
}

function batchKey(mesh: THREE.Mesh): string {
  const materials: THREE.Material[] = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  return `${mesh.geometry.uuid}|${materials.map((material) => material.uuid).join(",")}|${mesh.layers.mask}|${mesh.renderOrder}`;
}

function modelScale(object: THREE.Object3D, model: DecorModel): number {
  if (model.procedural) return 1;
  const size = aboveGroundBox(object).getSize(new THREE.Vector3());
  if ("height" in model.size) return model.size.height / size.y;
  if ("length" in model.size) return model.size.length / size.z;
  return model.size.width / size.x;
}

export function normalizeTemplate(scene: THREE.Object3D, model: DecorModel): { root: THREE.Object3D; bounds: DecorModelBounds } {
  const root = new THREE.Group();
  const inner = new THREE.Group();
  inner.add(scene);
  // Les modèles regardent vers +z ; le nord est -z dans cette scène (voir `render`).
  inner.rotation.y = Math.PI;
  root.add(inner);
  root.updateMatrixWorld(true);

  inner.scale.setScalar(modelScale(root, model));
  root.updateMatrixWorld(true);
  const box = aboveGroundBox(root);
  const center = box.getCenter(new THREE.Vector3());
  inner.position.set(-center.x, -box.min.y, -center.z);
  root.updateMatrixWorld(true);

  const placed = aboveGroundBox(root);
  return {
    root,
    bounds: { minX: placed.min.x, maxX: placed.max.x, minY: -placed.max.z, maxY: -placed.min.z, height: placed.max.y },
  };
}

/**
 * La surface horizontale soulevée de `height`, prolongée tout autour par une jupe qui descend jusqu'à
 * `skirtHeight` en s'écartant de `skirtWidth` : tout regard vers la surface d'origine la traverse
 * au-dessus de `skirtHeight`, même tout près de son bord.
 */
function liftedWithSkirt(geometry: THREE.BufferGeometry, height: number, skirtHeight: number, skirtWidth: number): THREE.BufferGeometry {
  const source = geometry.index ? geometry.toNonIndexed() : geometry;
  const position = source.getAttribute("position");
  const vertex = (index: number) => new THREE.Vector3().fromBufferAttribute(position, index);
  const key = (point: THREE.Vector3) => `${point.x.toFixed(4)},${point.z.toFixed(4)}`;
  const edges = new Map<string, { a: THREE.Vector3; b: THREE.Vector3; inside: THREE.Vector3; count: number }>();
  const points: number[] = [];
  const at = (point: THREE.Vector3, y: number) => [point.x, point.y + y, point.z];
  for (let i = 0; i + 2 < position.count; i += 3) {
    const triangle = [vertex(i), vertex(i + 1), vertex(i + 2)];
    points.push(...triangle.flatMap((point) => at(point, height)));
    for (let k = 0; k < 3; k++) {
      const a = triangle[k]!;
      const b = triangle[(k + 1) % 3]!;
      const id = [key(a), key(b)].sort().join("|");
      const edge = edges.get(id);
      if (edge) edge.count++;
      else edges.set(id, { a, b, inside: triangle[(k + 2) % 3]!, count: 1 });
    }
  }
  for (const { a, b, inside, count } of edges.values()) {
    if (count > 1) continue;
    const along = new THREE.Vector3(b.x - a.x, 0, b.z - a.z).normalize();
    const outward = new THREE.Vector3(-along.z, 0, along.x);
    if (outward.dot(new THREE.Vector3(inside.x - a.x, 0, inside.z - a.z)) > 0) outward.negate();
    const reach = outward.multiplyScalar(skirtWidth);
    const overlap = along.multiplyScalar(skirtWidth);
    const outerA = a.clone().add(reach).sub(overlap);
    const outerB = b.clone().add(reach).add(overlap);
    points.push(...at(a, height), ...at(b, height), ...at(outerB, skirtHeight), ...at(a, height), ...at(outerB, skirtHeight), ...at(outerA, skirtHeight));
  }
  return new THREE.BufferGeometry().setAttribute("position", new THREE.Float32BufferAttribute(points, 3));
}

function stencilled(material: THREE.Material, settings: Partial<THREE.Material>): THREE.Material {
  return Object.assign(withShader(material.clone(), material), settings);
}

/**
 * Une trémie (`Mask` + `Pit…` + `Rim` dans le modèle) se dessine à travers le sol, avant le reste du
 * décor : l'ouverture marque le pochoir là où elle est visible, la profondeur y est remise au fond,
 * puis la fosse, qui est sous le sol, y apparaît — et seulement là. Le décor dessiné ensuite passe
 * devant elle par le seul test de profondeur.
 */
function prepareOpening(root: THREE.Object3D): boolean {
  const mask = root.getObjectByName("Mask");
  if (!(mask instanceof THREE.Mesh)) return false;
  const bit = OPENING_STENCIL_BIT;

  // Sans test de profondeur : posée sur une dalle, l'ouverture s'y confondrait selon le zoom.
  mask.material = stencilled(mask.material as THREE.Material, {
    colorWrite: false, depthWrite: false, depthTest: false, stencilWrite: true, stencilRef: bit, stencilFuncMask: bit, stencilWriteMask: bit,
    stencilFunc: THREE.AlwaysStencilFunc, stencilZPass: THREE.ReplaceStencilOp,
  });
  mask.layers.set(LAYER_MASK);

  const reset = new THREE.Mesh(mask.geometry, stencilled(mask.material as THREE.Material, {
    depthTest: true, depthWrite: true, depthFunc: THREE.AlwaysDepth, stencilWriteMask: 0,
    stencilFunc: THREE.EqualStencilFunc, stencilZPass: THREE.KeepStencilOp,
  }));
  reset.layers.set(LAYER_DEPTH_RESET);
  mask.add(reset);

  const occluder = new THREE.Mesh(liftedWithSkirt(mask.geometry, OPENING_OCCLUDER_HEIGHT, DECOR_GROUND_MAX_HEIGHT, OPENING_OCCLUDER_SKIRT_WIDTH), stencilled(mask.material as THREE.Material, {
    side: THREE.DoubleSide, depthTest: true, stencilFunc: THREE.AlwaysStencilFunc,
    stencilZPass: THREE.KeepStencilOp, stencilZFail: THREE.ZeroStencilOp,
  }));
  occluder.layers.set(LAYER_MASK_OCCLUSION);
  mask.add(occluder);

  root.traverse((node) => {
    if (node instanceof THREE.Mesh && isInPit(node)) asPitMesh(node);
  });
  return true;
}

/** Ce qui est sous le sol d'une ouverture ne se dessine qu'à travers elle (bit du pochoir), à sa passe. */
function asPitMesh(node: THREE.Mesh): void {
  const bit = OPENING_STENCIL_BIT;
  node.layers.set(LAYER_PIT);
  const pitMaterial = (material: THREE.Material) => stencilled(material, {
    stencilWrite: true, stencilRef: bit, stencilFuncMask: bit, stencilWriteMask: 0,
    stencilFunc: THREE.EqualStencilFunc, stencilZPass: THREE.KeepStencilOp,
  });
  node.material = Array.isArray(node.material) ? (node.material as THREE.Material[]).map(pitMaterial) : pitMaterial(node.material as THREE.Material);
}

/** Un bord de zone creusée à moins de cette distance d'une trémie s'ouvre sur elle. */
const OPEN_EDGE_TOLERANCE_M = 0.3;

function distanceToSegment([px, pz]: GroundPoint, [ax, az]: GroundPoint, [bx, bz]: GroundPoint): number {
  const dx = bx - ax;
  const dz = bz - az;
  const lengthSq = dx * dx + dz * dz;
  const t = lengthSq ? Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / lengthSq)) : 0;
  return Math.hypot(px - (ax + t * dx), pz - (az + t * dz));
}

function isNearPolygon(point: GroundPoint, polygon: GroundPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, zi] = polygon[i]!;
    const [xj, zj] = polygon[j]!;
    if (distanceToSegment(point, [xi, zi], [xj, zj]) <= OPEN_EDGE_TOLERANCE_M) return true;
    if (zi > point[1] !== zj > point[1] && point[0] < ((xj - xi) * (point[1] - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}

function proceduralSize(model: DecorModel, item: DecorItem): [number, number] {
  const defaults = model.size as { width?: number; length?: number };
  return [roundTenth(item.width ?? defaults.width ?? 4), roundTenth(item.length ?? defaults.length ?? 8)];
}

/** Dénivelé d'un escalator ou d'un escalier posé à l'air libre. */
function flightRise(model: DecorModel, item: DecorItem): number {
  return roundTenth(item.depth ?? defaultFlightRise(model));
}

/** Pente d'un escalier libre, arrondie au degré : un gabarit par pente. */
function flightSlope(item: DecorItem): number | undefined {
  return item.slopeDeg === undefined ? undefined : Math.round(item.slopeDeg);
}

/** Scène, chaises ou tables : construits à leurs dimensions, sans rien d'une volée d'escalier. */
function isFurnishingModel(model: DecorModel): boolean {
  return model.procedural === "stage" || model.procedural === "seating" || model.procedural === "picnic" || model.procedural === "fence" || model.procedural === "rock-massif";
}

/** Hauteur d'un massif rocheux, arrondie au décimètre : un gabarit par hauteur. */
function massifHeight(model: DecorModel, item: DecorItem): number {
  return roundTenth(item.height ?? model.defaultHeight ?? 10);
}

/** Empreinte courte d'une chaîne : une image en data URI peut peser des centaines de kilo-octets. */
function hashString(value: string): string {
  let hash = 5381;
  for (let i = 0; i < value.length; i++) hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0;
  return `${(hash >>> 0).toString(36)}-${value.length}`;
}

function roundTenth(value: number): number {
  return Math.round(value * 10) / 10;
}

export class DecorLayer implements maplibregl.CustomLayerInterface {
  readonly id = DECOR_LAYER_ID;
  readonly type = "custom";
  readonly renderingMode = "3d";

  private map: maplibregl.Map | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.Camera();
  private readonly raycaster = new THREE.Raycaster();
  private readonly loader = new GLTFLoader();
  private readonly templates = new Map<string, DecorTemplate>();
  private readonly loading = new Set<string>();
  private readonly instances = new Map<string, DecorInstance>();
  private readonly batches = new Map<string, DecorBatch>();
  private readonly moverBatches = new Map<string, DecorBatch>();
  private readonly vatBatches = new Map<string, { mesh: THREE.InstancedMesh; capacity: number }>();
  private items: DecorItem[] = [];
  private itemsById = new Map<string, DecorItem>();
  /** Revoir tous les objets à la prochaine image ; sinon, seuls les actifs le sont. */
  private fullPassNeeded = true;
  private activeIds: string[] = [];
  /** Objets fixes hors des lots : revus quand le zoom change de palier. */
  private looseIds: string[] = [];
  private lodStep: number | null = null;
  private readonly chunkMembers = new Map<string, Set<string>>();
  private readonly dirtyChunks = new Set<string>();
  private readonly smokeSource = new THREE.Vector3();
  private openingInstances = 0;
  private grounds: DecorGround[] = [];
  private groundIndex = new DecorGroundIndex([]);
  private openingGroundIndex = new DecorGroundIndex([]);
  /** Change avec le sol et les volées : un objet immobile n'est replacé qu'alors. */
  private groundsVersion = 0;
  private readonly placed = new Map<string, { item: DecorItem; root: THREE.Object3D; groundsVersion: number; hidden: boolean; floating: boolean; batched: boolean; chunk: string | null; lodMetersPerPixel: number }>();
  /** Les lots ne sont recomposés que si un objet a été replacé, teinté, ajouté ou retiré. */
  private batchesDirty = true;
  private lastBatchSync = 0;
  private flights: DecorFlight[] = [];
  private pits: DecorPit[] = [];
  private pitsKey = "";
  private pitsViewStep: string | null = null;
  private pitDecalsStale = false;
  private pitsViewStepAt = 0;
  private readonly pitRoots: THREE.Object3D[] = [];
  private pitsMerged: THREE.Group | null = null;
  /** Les trémies et volées avec lesquelles les zones creusées ont été construites (`footprintsKey`). */
  private pitFootprintsKey = "";
  private readonly openingParticipants = new WeakMap<THREE.Object3D, boolean>();
  private pitRims: { style: RailingStyle; edges: RailingEdge[] }[] = [];
  private railings: DecorRailing[] = [];
  private railingsKey = "";
  private railingGapsKey = "";
  private railingRoot: THREE.Object3D | null = null;
  private preview: DecorItem | null = null;
  private highlight: DecorHighlight = { selectedId: null, hoveredId: null, color: "#000000" };
  /** Partagé par toutes les ombres de cette couche : le masquer les masque toutes, instanciées comprises. */
  private readonly contactShadowMaterial = createContactShadowMaterial();
  private readonly sunLight = new THREE.DirectionalLight(0xffffff, 1.8);
  private readonly skyLight = new THREE.HemisphereLight(0xffffff, 0x8a8f80, 1.6);
  private readonly plainMaterials = new WeakMap<THREE.Mesh, THREE.Material | THREE.Material[]>();
  private readonly recolored = new Map<string, { material: THREE.Material; users: number }>();
  /** Matériaux recolorés que plus aucun objet n'utilise : libérés une fois les lots refaits sans eux. */
  private readonly unusedRecolored = new Set<string>();
  private previewInstance: DecorInstance | null = null;
  private origin: maplibregl.MercatorCoordinate | null = null;
  private meterScale = 1;
  private hasProjection = false;
  private lastFrameTime = performance.now();
  /** Temps écoulé pour les objets qui se déplacent : il s'arrête quand ils sont figés, pour qu'ils repartent d'où ils étaient. */
  private movingClock = 0;
  private readonly waterRipples = new WaterRipples();
  private readonly surfaceScatter = new SurfaceScatter(() => this.map?.triggerRepaint());
  private scatterAreas: DecorScatterArea[] = [];
  private scatterKey = "";
  private pendingScatterRoads: RoadFeature[] | null | undefined;
  private roofs: DecorSurfaceRoof[] = [];
  private roofsKey = "";
  private roofRoot: THREE.Group | null = null;
  private readonly roofMaterials = new Map<THREE.Texture, THREE.Material>();
  private waterClock = 0;
  private animationRequest: number | null = null;
  /** Durée d'une image d'écran, mesurée au premier passage. */
  private screenFrameMs: number | null = null;
  private powerSaving = false;
  private animated: boolean;
  private readonly stats = new RenderStatsWindow(performance.now());
  private lastFrameCounts = { active: 0, animated: 0, drawCalls: 0, triangles: 0 };
  private visibleCounts = { visibleInstances: 0, impostors: 0 };
  private readonly groundSeal = new THREE.Mesh(
    new THREE.PlaneGeometry(GROUND_SEAL_SIZE_M, GROUND_SEAL_SIZE_M).rotateX(-Math.PI / 2),
    new THREE.MeshBasicMaterial({
      colorWrite: false, side: THREE.DoubleSide, stencilWrite: true, stencilRef: OPENING_STENCIL_BIT,
      stencilFuncMask: OPENING_STENCIL_BIT, stencilWriteMask: 0, stencilFunc: THREE.NotEqualStencilFunc,
      // Repoussé en profondeur : les taches d'ombre, à quelques centimètres du sol, ne clignotent plus contre lui.
      polygonOffset: true, polygonOffsetFactor: 4, polygonOffsetUnits: 4,
    }),
  );

  constructor(private readonly options: DecorLayerOptions) {
    this.animated = options.animated;
    this.groundSeal.layers.set(LAYER_GROUND_SEAL);
    this.scene.add(this.groundSeal);
    this.scene.add(this.waterRipples.group);
    this.scene.add(this.surfaceScatter.root);
    this.surfaceScatter.root.matrixWorldAutoUpdate = false;
    this.setSun({ azimuthDeg: 315, angleDeg: 28 });
    for (const light of [this.skyLight, this.sunLight]) {
      light.layers.enableAll();
      this.scene.add(light);
    }
  }

  onAdd(map: maplibregl.Map, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    this.map = map;
    this.renderer = new THREE.WebGLRenderer({ canvas: map.getCanvas(), context: gl, antialias: true });
    this.renderer.autoClear = false;
    this.renderer.info.autoReset = false;
    map.on("moveend", this.handleMoveEnd);
  }

  private readonly handleMoveEnd = (): void => {
    if (this.pitDecalsStale || this.batchesDirty) this.map?.triggerRepaint();
  };

  onRemove(): void {
    if (this.animationRequest !== null) cancelAnimationFrame(this.animationRequest);
    this.animationRequest = null;
    for (const instance of this.instances.values()) this.disposeInstance(instance);
    this.instances.clear();
    for (const batch of [...this.batches.values(), ...this.moverBatches.values()]) this.disposeBatch(batch);
    this.batches.clear();
    this.moverBatches.clear();
    for (const batch of this.vatBatches.values()) this.disposeVatBatch(batch);
    this.vatBatches.clear();
    if (this.previewInstance) this.disposeInstance(this.previewInstance);
    this.previewInstance = null;
    this.waterRipples.dispose();
    this.surfaceScatter.dispose();
    this.renderer?.dispose();
    this.renderer = null;
    this.map?.off("moveend", this.handleMoveEnd);
    this.map = null;
  }

  setItems(items: DecorItem[]): void {
    this.items = items;
    this.itemsById = new Map(items.map((item) => [item.id, item]));
    this.fullPassNeeded = true;
    for (const [id, placed] of this.placed) {
      if (this.itemsById.has(id)) continue;
      this.moveToChunk(id, placed.chunk, null, true);
      this.placed.delete(id);
    }
    this.ensureOrigin();
    this.syncInstances();
    this.buildFlights();
    // Les zones creusées ne dépendent des objets que par les trémies et volées qui les percent.
    if (this.pits.length) {
      if (this.footprintsKey() !== this.pitFootprintsKey) this.buildPits();
    } else if (this.railings.length && JSON.stringify(this.flightFootprints()) !== this.railingGapsKey) this.buildRailingRoot();
    this.map?.triggerRepaint();
  }

  /** À l'arrêt, chaque objet revient à sa place, celle que l'on édite. */
  setAnimated(animated: boolean): void {
    if (animated === this.animated) return;
    this.animated = animated;
    this.movingClock = 0;
    this.fullPassNeeded = true;
    this.map?.triggerRepaint();
  }

  hasAnimations(): boolean {
    return this.items.some((item) => {
      const model = this.options.models[item.model];
      return (!!model && isDecorModelMovable(model) && (item.path?.length ?? 0) >= 2) || !!this.instances.get(item.id)?.mixer;
    });
  }

  /** Fige les animations, chaque objet là où il en est, pour épargner la batterie. */
  setPowerSaving(powerSaving: boolean): void {
    if (powerSaving === this.powerSaving) return;
    this.powerSaving = powerSaving;
    this.fullPassNeeded = true;
    this.map?.triggerRepaint();
  }

  setContactShadowsVisible(visible: boolean): void {
    if (this.contactShadowMaterial.visible === visible) return;
    this.contactShadowMaterial.visible = visible;
    this.map?.triggerRepaint();
  }

  /** La scène porte l'est en x, le haut en y et le sud en z. */
  setSun(sun: DecorSun): void {
    const azimuth = (sun.azimuthDeg * Math.PI) / 180;
    const elevation = ((90 - sun.angleDeg) * Math.PI) / 180;
    this.sunLight.position.set(Math.sin(azimuth) * Math.cos(elevation), Math.sin(elevation), -Math.cos(azimuth) * Math.cos(elevation));
    this.map?.triggerRepaint();
  }

  setTint(tint: { decorSun: string; decorSky: string; decorGround: string }): void {
    this.sunLight.color.set(tint.decorSun);
    this.skyLight.color.set(tint.decorSky);
    this.skyLight.groundColor.set(tint.decorGround);
    this.map?.triggerRepaint();
  }

  setHighlight(highlight: DecorHighlight): void {
    const { selectedId, hoveredId, color } = this.highlight;
    if (highlight.selectedId === selectedId && highlight.hoveredId === hoveredId && highlight.color === color) return;
    this.highlight = highlight;
    this.fullPassNeeded = true;
    this.map?.triggerRepaint();
  }

  /** Les parties recolorables du modèle de cet objet ; `null` tant que le modèle n'est pas chargé. */
  colorSlots(item: DecorItem): DecorColorSlot[] | null {
    const template = this.templates.get(this.templateKey(item));
    return template ? template.colorSlots.map(({ id, color, share, label }) => ({ id, color, share, ...(label ? { label } : {}) })) : null;
  }

  /**
   * Les matériaux aux couleurs de l'objet, partagés entre les objets aux mêmes couleurs : les objets
   * fixes recolorés à l'identique restent dans un même lot.
   */
  private applyColors(item: DecorItem, instance: DecorInstance): void {
    const signature = colorsSignature(item.colors);
    if (signature === instance.colors) return;
    instance.colors = signature;
    this.releaseRecolored(instance);
    const slots = this.templates.get(instance.modelId)?.colorSlots ?? [];
    const recolor = (material: THREE.Material) => {
      if (!signature) return material;
      const key = `${material.uuid}|${signature}`;
      let entry = this.recolored.get(key);
      if (!entry) {
        const copy = recolorMaterial(material, slots, item.colors!);
        if (copy === material) return material;
        this.recolored.set(key, (entry = { material: copy, users: 0 }));
      }
      entry.users++;
      this.unusedRecolored.delete(key);
      instance.recoloredKeys.push(key);
      return entry.material;
    };
    instance.root.traverse((node) => {
      if (!(node instanceof THREE.Mesh) || node.name === CONTACT_SHADOW_NODE) return;
      const original: THREE.Material | THREE.Material[] = node.userData.originalMaterial ?? this.plainMaterials.get(node) ?? node.material;
      node.userData.originalMaterial = original;
      const plain = Array.isArray(original) ? original.map(recolor) : recolor(original);
      // La teinte de survol était une copie de l'ancien matériau : elle est refaite juste après.
      if (instance.highlight !== "none") for (const material of [node.material].flat()) material.dispose();
      this.plainMaterials.set(node, plain);
      node.material = plain;
    });
    instance.highlight = "none";
    this.applyHighlight(item, instance);
    this.batchesDirty = true;
  }

  private releaseRecolored(instance: DecorInstance): void {
    for (const key of instance.recoloredKeys) {
      const entry = this.recolored.get(key);
      if (entry && --entry.users <= 0) this.unusedRecolored.add(key);
    }
    instance.recoloredKeys = [];
  }

  private disposeUnusedRecolored(): void {
    for (const key of this.unusedRecolored) {
      const entry = this.recolored.get(key);
      if (entry && entry.users <= 0) {
        entry.material.dispose();
        this.recolored.delete(key);
      }
    }
    this.unusedRecolored.clear();
  }

  private applyHighlight(item: DecorItem, instance: DecorInstance): void {
    const state: DecorHighlightState =
      item.id === this.highlight.selectedId ? "selected" : item.id === this.highlight.hoveredId ? "hovered" : "none";
    if (instance.highlight === state) return;
    instance.highlight = state;
    this.batchesDirty = true;
    const tint = new THREE.Color(this.highlight.color);
    instance.root.traverse((node) => {
      if (!(node instanceof THREE.Mesh) || node.name === CONTACT_SHADOW_NODE) return;
      const plain: THREE.Material | THREE.Material[] = this.plainMaterials.get(node) ?? node.material;
      this.plainMaterials.set(node, plain);
      const plainList: THREE.Material[] = Array.isArray(plain) ? plain : [plain];
      if (node.material !== plain) {
        const current: THREE.Material[] = Array.isArray(node.material) ? node.material : [node.material];
        for (const material of current) if (!plainList.includes(material)) material.dispose();
      }
      if (state === "none") {
        node.material = plain;
        return;
      }
      const tinted = (material: THREE.Material) => {
        if ("emissive" in material && material.emissive instanceof THREE.Color) {
          const copy = withShader(material.clone(), material) as THREE.MeshStandardMaterial;
          copy.emissive = tint;
          copy.emissiveIntensity = HIGHLIGHT_INTENSITY[state];
          return copy;
        }
        // Un matériau sans éclairage (KHR_materials_unlit) n'a pas d'émissif : on teinte sa couleur.
        if ("color" in material && material.color instanceof THREE.Color) {
          const copy = withShader(material.clone(), material) as THREE.MeshBasicMaterial;
          copy.color = copy.color.clone().lerp(tint, UNLIT_HIGHLIGHT_MIX[state]);
          return copy;
        }
        return material;
      };
      node.material = Array.isArray(plain) ? plainList.map(tinted) : tinted(plain);
    });
  }

  setPreview(item: DecorItem | null): void {
    this.preview = item;
    this.ensureOrigin();
    if (this.previewInstance && (!item || this.previewInstance.modelId !== this.templateKey(item))) {
      this.disposeInstance(this.previewInstance);
      this.previewInstance = null;
    }
    if (item) this.loadModel(item);
    this.map?.triggerRepaint();
  }

  setPits(pits: DecorPit[]): void {
    const key = JSON.stringify(pits);
    if (key === this.pitsKey) return;
    this.pitsKey = key;
    this.pits = pits;
    this.fullPassNeeded = true;
    this.ensureOrigin();
    this.buildPits();
    this.map?.triggerRepaint();
  }

  setSurfaceRoofs(roofs: DecorSurfaceRoof[]): void {
    const key = JSON.stringify(roofs);
    if (key === this.roofsKey) return;
    this.roofsKey = key;
    this.roofs = roofs;
    this.ensureOrigin();
    if (this.roofRoot) {
      this.scene.remove(this.roofRoot);
      this.roofRoot.traverse((node) => {
        if (node instanceof THREE.Mesh) node.geometry.dispose();
      });
      this.roofRoot = null;
    }
    if (!this.origin || !roofs.length) return;
    const local = (position: LngLat): GroundPoint => this.toLocal(position);
    const root = new THREE.Group();
    root.name = "SurfaceRoofs";
    for (const roof of roofs) {
      const texture = surfaceTexture3d(roof.surface, roof.tint ?? null);
      let material = this.roofMaterials.get(texture);
      if (!material) {
        material = new THREE.MeshBasicMaterial({ map: texture, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
        this.roofMaterials.set(texture, material);
      }
      root.add(new THREE.Mesh(flatSurface(roof.ring.map(local), roof.holes.map((hole) => hole.map(local)), roof.top + ROOF_SURFACE_LIFT_M), material));
    }
    root.updateMatrixWorld(true);
    root.matrixWorldAutoUpdate = false;
    this.scene.add(root);
    this.roofRoot = root;
    this.map?.triggerRepaint();
  }

  /** Géométries et textures du décor envoyées au GPU, estimées (voir `sceneMemoryBytes`), poses des foules comprises. */
  estimateMemoryBytes(): number {
    const poses = [...this.templates.values()].flatMap((template) => (template.vat && template.vat !== "pending" ? template.vat.meshes.flatMap((mesh) => [mesh.positions, mesh.normals]) : []));
    return sceneMemoryBytes([this.scene], poses);
  }

  /** Faux tant que la caméra est trop loin pour qu'un seul élément semé soit dessiné. */
  get scatterDrawn(): boolean {
    return this.surfaceScatter.drawn > 0;
  }

  setSurfaceScatter(areas: DecorScatterArea[]): void {
    const key = `${JSON.stringify(areas)}|${this.pitsKey}`;
    if (key === this.scatterKey) return;
    this.scatterKey = key;
    this.scatterAreas = areas;
    this.ensureOrigin();
    if (!this.origin) return;
    if (this.pendingScatterRoads !== undefined) this.setScatterRoads(this.pendingScatterRoads);
    const local = (position: LngLat): GroundPoint => this.toLocal(position);
    this.surfaceScatter.update(
      areas.map((area) => ({ ...area, ring: area.ring.map(local), holes: area.holes.map((hole) => hole.map(local)) })),
      this.pits.map((pit) => pit.ring.map(local)),
    );
  }

  /** Les routes OSM chargées : l'habillage des formes qui le demandent (`roadBounds`) ne pousse pas dessus. */
  setScatterRoads(roads: RoadFeature[] | null): void {
    this.ensureOrigin();
    if (!this.origin) {
      this.pendingScatterRoads = roads;
      return;
    }
    this.pendingScatterRoads = undefined;
    this.surfaceScatter.setRoads(roads, { x: this.origin.x, y: this.origin.y, meterScale: this.meterScale });
  }

  setRailings(railings: DecorRailing[]): void {
    const key = JSON.stringify(railings);
    if (key === this.railingsKey) return;
    this.railingsKey = key;
    this.railings = railings;
    this.ensureOrigin();
    this.buildRailingRoot();
    this.map?.triggerRepaint();
  }

  private toLocal(position: LngLat): GroundPoint {
    const origin = this.origin!;
    const point = maplibregl.MercatorCoordinate.fromLngLat(position, 0);
    return [(point.x - origin.x) / this.meterScale, (point.y - origin.y) / this.meterScale];
  }

  private railingEdges({ lines, closed, base }: DecorRailing, gaps: GroundPoint[][]): RailingEdge[] {
    return lines.flatMap((line) => outlineEdges(line.map((point) => this.toLocal(point)), closed, base).flatMap((edge) => edgeOutsideGaps(edge, gaps)));
  }

  /** Toutes les barrières au niveau du sol ou au-dessus, en un seul objet : un ou deux appels de dessin par finition pour toute la carte. */
  private buildRailingRoot(): void {
    if (this.railingRoot) {
      this.scene.remove(this.railingRoot);
      this.railingRoot.traverse((node) => {
        if (node instanceof THREE.Mesh) node.geometry.dispose();
      });
      this.railingRoot = null;
    }
    if (!this.origin) return;
    const gaps = this.flightFootprints();
    this.railingGapsKey = JSON.stringify(gaps);
    const groups = [...this.pitRims.filter(Boolean), ...this.railings.map((railing) => ({ style: railing.style, edges: this.railingEdges(railing, gaps) }))];
    if (!groups.some((group) => group.edges.length)) return;
    const root = buildRailings(groups, "Railing");
    root.updateMatrixWorld(true);
    this.scene.add(root);
    this.railingRoot = root;
  }

  /** Mètres par pixel au fond d'une zone creusée : plus loin de la caméra que le sol, d'autant plus que la vue est rasante. */
  private metersPerPixel(latitude: number, depth: number): number {
    const map = this.map;
    if (!map) return 0;
    const zoom = Math.round(map.getZoom() / DECAL_ZOOM_STEP) * DECAL_ZOOM_STEP;
    const pitch = (Math.round(map.getPitch() / DECAL_PITCH_STEP_DEG) * DECAL_PITCH_STEP_DEG * Math.PI) / 180;
    const onGround = (EARTH_CIRCUMFERENCE_M * Math.cos((latitude * Math.PI) / 180)) / (512 * 2 ** zoom);
    const cameraDistance = (map.getCanvas().clientHeight / 2 / Math.tan((map.getVerticalFieldOfView() * Math.PI) / 360)) * onGround;
    return onGround * (1 + depth / (Math.cos(pitch) * cameraDistance));
  }

  private buildPits(only?: ReadonlySet<number>): void {
    for (const [index, root] of this.pitRoots.entries()) {
      if (only && !only.has(index)) continue;
      this.scene.remove(root);
      root.traverse((node) => {
        if (!(node instanceof THREE.Mesh)) return;
        node.geometry.dispose();
        for (const material of Array.isArray(node.material) ? node.material : [node.material]) (material as THREE.Material).dispose();
      });
    }
    if (!only) {
      this.pitRoots.length = 0;
      this.pitRims = [];
    }
    if (!this.origin) return;
    const local = (position: LngLat): GroundPoint => this.toLocal(position);
    const openings = this.openingFootprints();
    const flights = this.flightFootprints();
    if (!only) this.pitFootprintsKey = JSON.stringify([openings, flights]);
    const rings = this.pits.map((pit) => pit.ring.map(local));
    for (const [index, pit] of this.pits.entries()) {
      if (only && !only.has(index)) continue;
      // Un bord commun à une autre zone creusée ne descend que de son fond à celui-ci ; la moins
      // profonde des deux n'y a pas de paroi.
      const wallTop = (a: GroundPoint, b: GroundPoint): number | null => {
        const middle: GroundPoint = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
        if (openings.some((footprint) => isNearPolygon(middle, footprint))) return null;
        let top = pit.rim ?? 0;
        rings.forEach((ring, other) => {
          if (other === index || !isNearPolygon(middle, ring)) return;
          top = Math.min(top, -this.pits[other]!.depth);
        });
        return top <= -pit.depth ? null : top;
      };
      const { root, rim } = buildDugArea(rings[index]!, pit.holes.map((hole) => hole.map(local)), {
        depth: pit.depth,
        rim: pit.rim ?? 0,
        floorHoles: (pit.floorHoles ?? []).map((hole) => hole.map(local)),
        floorColor: pit.floorColor,
        ...(pit.floorSurface ? { floorTexture: surfaceTexture3d(pit.floorSurface, pit.floorTint ?? null) } : {}),
        railing: pit.railing,
        water: pit.water,
        railingGaps: [...flights, ...(pit.openings ?? []).flatMap((path) => pathGaps(path.map(local)))],
        railings: (pit.railings ?? []).map((railing) => ({ style: railing.style, edges: this.railingEdges(railing, flights) })),
        wallTop,
        decals: this.localDecals(pit),
      });
      prepareOpening(root);
      root.updateMatrixWorld(true);
      this.pitRoots[index] = root;
      if (pit.railing) this.pitRims[index] = { style: pit.railing, edges: rim };
    }
    this.mergePits();
    if (!only) this.buildRailingRoot();
  }

  /**
   * Les zones creusées ne bougent jamais : leurs parois, fonds, eaux et masques sont dessinés fondus,
   * quelques maillages pour toute la carte au lieu d'une dizaine par zone.
   */
  private mergePits(): void {
    if (this.pitsMerged) {
      this.scene.remove(this.pitsMerged);
      disposeMerged(this.pitsMerged);
      this.pitsMerged = null;
    }
    const roots = this.pitRoots.filter(Boolean);
    if (!roots.length) return;
    this.pitsMerged = mergeStaticRoots(roots, "Pits");
    this.scene.add(this.pitsMerged);
  }

  private footprintsKey(): string {
    return JSON.stringify([this.openingFootprints(), this.flightFootprints()]);
  }

  /** Les formes posées au fond de la zone, dans le repère de la scène, leurs bordures en pixels converties au zoom courant. */
  private localDecals(pit: DecorPit) {
    const local = (position: LngLat): GroundPoint => this.toLocal(position);
    return (pit.decals ?? []).map((decal) => {
      const borderWidth = decal.borderWidthUnit === "m" ? decal.borderWidth : decal.borderWidth * this.metersPerPixel(decal.ring[0]![1], pit.depth);
      return {
        ring: decal.ring.map(local),
        holes: decal.holes.map((hole) => hole.map(local)),
        closed: decal.closed,
        fillColor: decal.fillColor,
        borderColor: decal.borderColor,
        borderWidth,
        volume: decal.volume,
        ...(decal.borderDash ? { borderDash: [decal.borderDash[0] * borderWidth, decal.borderDash[1] * borderWidth] as [number, number] } : {}),
      };
    });
  }

  /**
   * Refait les seules formes posées au fond de ces zones, pas leurs parois ni leur eau : sur un grand
   * plan d'eau, tout reconstruire coûtait une quarantaine de millisecondes à chaque palier de zoom.
   */
  private rebuildPitDecals(indexes: ReadonlySet<number>): void {
    for (const index of indexes) {
      const pit = this.pits[index];
      const holder = this.pitRoots[index]?.getObjectByName("Pit");
      if (!pit || !holder) continue;
      for (const old of holder.children.filter((child) => child.name === "PitDecal")) {
        holder.remove(old);
        if (old instanceof THREE.Mesh) {
          old.geometry.dispose();
          for (const material of [old.material].flat()) (material as THREE.Material).dispose();
        }
      }
      for (const decal of buildPitDecals(this.localDecals(pit), pit.depth)) {
        if (decal instanceof THREE.Mesh) asPitMesh(decal);
        holder.add(decal);
      }
      holder.updateMatrixWorld(true);
    }
    this.mergePits();
  }

  /** L'emprise au sol des escaliers et escalators posés à l'air libre, dans le repère de la scène. */
  private flightFootprints(): GroundPoint[][] {
    const footprints: GroundPoint[][] = [];
    for (const item of this.items) {
      const model = this.options.models[item.model];
      if (!model || !isFreeFlight(model)) continue;
      const bounds = this.templates.get(this.templateKey(item))?.bounds;
      if (!bounds) continue;
      const holder = new THREE.Object3D();
      this.place(holder, item.position, item.rotationDeg, item.scale);
      footprints.push(
        [[bounds.minX, -bounds.maxY], [bounds.maxX, -bounds.maxY], [bounds.maxX, -bounds.minY], [bounds.minX, -bounds.minY]].map(([x, z]) => {
          const corner = new THREE.Vector3(x, 0, z).applyMatrix4(holder.matrixWorld);
          return [corner.x, corner.z] as GroundPoint;
        }),
      );
    }
    return footprints;
  }

  /** L'ouverture de chaque trémie posée, au sol, dans le repère de la scène. */
  private openingFootprints(): GroundPoint[][] {
    const footprints: GroundPoint[][] = [];
    for (const item of this.items) {
      const template = this.templates.get(this.templateKey(item));
      const mask = template?.opening ? template.root.getObjectByName("Mask") : null;
      if (!(mask instanceof THREE.Mesh)) continue;
      template!.root.updateMatrixWorld(true);
      mask.geometry.computeBoundingBox();
      const box = mask.geometry.boundingBox!;
      const holder = new THREE.Object3D();
      this.place(holder, item.position, item.rotationDeg, item.scale);
      const matrix = holder.matrixWorld.clone().multiply(mask.matrixWorld);
      footprints.push(
        [[box.min.x, box.min.z], [box.max.x, box.min.z], [box.max.x, box.max.z], [box.min.x, box.max.z]].map(([x, z]) => {
          const corner = new THREE.Vector3(x, 0, z).applyMatrix4(matrix);
          return [corner.x, corner.z] as GroundPoint;
        }),
      );
    }
    return footprints;
  }

  setGrounds(grounds: DecorGround[]): void {
    this.grounds = grounds;
    this.groundIndex = new DecorGroundIndex(grounds);
    this.openingGroundIndex = new DecorGroundIndex(grounds.filter((ground) => ground.top > 0));
    this.groundsVersion++;
    this.buildFlights();
    this.map?.triggerRepaint();
  }

  /** Hauteur où se pose un objet à cette position : volée, dalle, toit d'un volume, ou fond d'une zone creusée. */
  groundHeightAt(position: LngLat, modelId?: string): number {
    if (modelId && this.options.models[modelId]?.opening) return this.openingGroundHeight(position);
    return flightAt(position, this.flights)?.height ?? this.groundIndex.heightAt(position);
  }

  /** Le fond de la zone creusée la plus profonde, 0 sans zone creusée. */
  lowestGroundHeight(): number {
    return this.grounds.reduce((lowest, ground) => Math.min(lowest, ground.top), 0);
  }

  /** Les escaliers et escalators posés, vus comme un sol en pente que l'on peut emprunter. */
  private buildFlights(): void {
    this.groundsVersion++;
    this.fullPassNeeded = true;
    this.flights = this.items.flatMap((item) => {
      const model = this.options.models[item.model];
      const bounds = this.templates.get(this.templateKey(item))?.bounds;
      if (!model?.procedural || !bounds || isFurnishingModel(model) || isFixedProceduralModel(model)) return [];
      const profile = isFreeFlight(model)
        ? freeFlightProfile(model, flightRise(model, item), flightSlope(item))
        : stairsOpeningProfile(proceduralSize(model, item)[1], { depth: item.depth ? roundTenth(item.depth) : undefined });
      return [{
        position: item.position,
        rotationDeg: item.rotationDeg,
        scale: item.scale,
        minX: bounds.minX,
        maxX: bounds.maxX,
        minY: bounds.minY,
        maxY: bounds.maxY,
        profile,
        base: this.groundOf(item, item.position),
        escalator: model.procedural === "escalator-open",
      }];
    });
  }

  /** Une trémie s'ouvre au niveau du sol : elle ne descend pas au fond d'une zone creusée qu'elle borde. */
  /** `headingDeg` : le cap d'un objet en mouvement, pour qu'il ne prenne que les volées qu'il emprunte dans leur sens. */
  private elevationOf(item: DecorItem, position: LngLat, headingDeg?: number): number {
    if (item.elevation !== undefined) return item.elevation;
    if (!this.options.models[item.model]?.procedural) {
      const flight = flightAt(position, this.flights, headingDeg);
      if (flight) return flight.height;
    }
    return this.groundOf(item, position);
  }

  /** Le sol sous l'objet, volées exceptées : celui d'une volée elle-même. */
  private groundOf(item: DecorItem, position: LngLat): number {
    if (item.elevation !== undefined) return item.elevation;
    if (this.options.models[item.model]?.opening || this.templates.get(this.templateKey(item))?.opening) return this.openingGroundHeight(position);
    return this.groundIndex.heightAt(position);
  }

  private openingGroundHeight(position: LngLat): number {
    return this.openingGroundIndex.heightAt(position);
  }

  modelBounds(modelId: string): DecorModelBounds | null {
    return this.templates.get(modelId)?.bounds ?? null;
  }

  /** L'emprise de cet élément tel qu'il est construit : celle d'un escalier dépend de ses dimensions. */
  itemBounds(item: DecorItem): DecorModelBounds | null {
    return this.templates.get(this.templateKey(item))?.bounds ?? null;
  }

  /** Un modèle construit par le moteur a un gabarit par jeu de dimensions. */
  private templateKey(item: DecorItem): string {
    const model = this.options.models[item.model];
    if (!model?.procedural || isFixedProceduralModel(model)) return item.model;
    const [width, length] = proceduralSize(model, item);
    if (model.procedural === "rock-massif") return `${item.model}@${width}x${length}x${massifHeight(model, item)}`;
    if (isFurnishingModel(model)) return `${item.model}@${width}x${length}${item.image ? `#${hashString(item.image)}` : ""}`;
    if (model.procedural !== "stairs-opening") return `${item.model}@${width}-${flightRise(model, item)}${item.slopeDeg !== undefined ? `/${flightSlope(item)}` : ""}`;
    const depth = item.depth ? `-${roundTenth(item.depth)}` : "";
    return `${item.model}@${width}x${length}${depth}${item.openBottom ? "-open" : ""}`;
  }

  pick(point: { x: number; y: number }): string | null {
    if (!this.map || !this.hasProjection) return null;
    const canvas = this.map.getCanvas();
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    const candidates: { id: string; root: THREE.Object3D; area: number }[] = [];

    for (const item of this.items) {
      const instance = this.instances.get(item.id);
      const template = this.templates.get(this.templateKey(item));
      if (!instance || !template || !instance.root.visible) continue;

      const { minX, maxX, minY, maxY, height: top } = template.bounds;
      const corners: THREE.Vector3[] = [];
      for (const x of [minX, maxX]) for (const y of [minY, maxY]) for (const z of [0, top]) corners.push(new THREE.Vector3(x, z, -y));

      let left = Infinity;
      let right = -Infinity;
      let up = Infinity;
      let down = -Infinity;
      let behindCamera = false;
      for (const corner of corners) {
        corner.applyMatrix4(instance.root.matrixWorld);
        const clip = new THREE.Vector4(corner.x, corner.y, corner.z, 1).applyMatrix4(this.camera.projectionMatrix);
        // Un coin derrière la caméra se projette en miroir : sa boîte couvrirait n'importe quel point.
        if (clip.w <= 0) {
          behindCamera = true;
          break;
        }
        const sx = ((clip.x / clip.w + 1) / 2) * width;
        const sy = ((1 - clip.y / clip.w) / 2) * height;
        left = Math.min(left, sx);
        right = Math.max(right, sx);
        up = Math.min(up, sy);
        down = Math.max(down, sy);
      }
      if (behindCamera) continue;

      if (point.x >= left && point.x <= right && point.y >= up && point.y <= down) {
        candidates.push({ id: item.id, root: instance.root, area: (right - left) * (down - up) });
      }
    }
    if (!candidates.length) return null;

    // La boîte d'un arbre couvre tout ce qui pousse à son pied : on vise ce qui est dessiné, le plus
    // proche d'abord, et à défaut la plus petite boîte, celle de l'objet que l'on frôle — s'il est
    // petit à l'écran : celle d'une scène couvre le sol tout autour, qu'il faut pouvoir sélectionner.
    const ray = this.screenRay(point, width, height);
    if (ray) {
      this.raycaster.set(ray.origin, ray.direction);
      let hit: { id: string; distance: number } | null = null;
      for (const candidate of candidates) {
        const first = this.raycaster.intersectObject(candidate.root, true).find((intersection) => isShown(intersection.object));
        if (first && (!hit || first.distance < hit.distance)) hit = { id: candidate.id, distance: first.distance };
      }
      if (hit) return hit.id;
    }
    const grazed = candidates.filter((candidate) => candidate.area <= GRAZE_MAX_AREA_PX);
    if (!grazed.length) return null;
    return grazed.reduce((smallest, candidate) => (candidate.area < smallest.area ? candidate : smallest)).id;
  }

  /** Le rayon de la caméra qui passe par ce pixel, dans le repère de la scène. */
  private screenRay(point: { x: number; y: number }, width: number, height: number): THREE.Ray | null {
    const inverse = this.camera.projectionMatrix.clone().invert();
    const x = (point.x / width) * 2 - 1;
    const y = 1 - (point.y / height) * 2;
    const at = (z: number) => {
      const v = new THREE.Vector4(x, y, z, 1).applyMatrix4(inverse);
      return v.w ? new THREE.Vector3(v.x / v.w, v.y / v.w, v.z / v.w) : null;
    };
    const near = at(-1);
    const far = at(1);
    if (!near || !far) return null;
    const direction = far.sub(near);
    return direction.lengthSq() ? new THREE.Ray(near, direction.normalize()) : null;
  }

  render(_gl: WebGLRenderingContext | WebGL2RenderingContext, options: maplibregl.CustomRenderMethodInput): void {
    if (!this.renderer || !this.map || !this.origin) return;

    const now = performance.now();
    const delta = Math.min(MAX_ANIMATION_STEP_S, (now - this.lastFrameTime) / 1000);
    this.lastFrameTime = now;
    const zoom = this.map.getZoom();
    const viewStep = `${Math.round(zoom / DECAL_ZOOM_STEP)}:${Math.round(this.map.getPitch() / DECAL_PITCH_STEP_DEG)}`;
    if (viewStep !== this.pitsViewStep) {
      this.pitDecalsStale ||= this.pitsViewStep !== null;
      this.pitsViewStep = viewStep;
      this.pitsViewStepAt = now;
      // Une image après le calme, pour refaire les bordures même si plus rien ne bouge.
      setTimeout(() => this.map?.triggerRepaint(), PIT_DECALS_SETTLE_MS + 20);
    }
    const moving = this.map.isMoving();
    // Une bordure en pixels garde son épaisseur le temps du geste, et jusqu'à ce que le zoom se pose.
    if (this.pitDecalsStale && !moving && now - this.pitsViewStepAt >= PIT_DECALS_SETTLE_MS) {
      this.pitDecalsStale = false;
      const indexes = new Set(this.pits.flatMap((pit, index) => (pit.decals?.some((decal) => decal.borderWidthUnit === "px") ? [index] : [])));
      if (indexes.size) this.rebuildPitDecals(indexes);
    }
    const playing = this.animated && !this.powerSaving;
    const movingAnimated = playing && zoom >= MOVING_ANIMATION_MIN_ZOOM;
    const staticAnimated = playing && zoom >= STATIC_ANIMATION_MIN_ZOOM;
    if (movingAnimated) {
      this.movingClock += delta;
      this.waterClock += delta;
    }
    this.waterRipples.time = this.waterClock;
    windUniforms.uWindTime.value = this.waterClock;
    waterUniforms.uWaterDetail.value = waterDetailShare(zoom);
    this.waterRipples.begin();
    const elapsed = this.animated ? this.movingClock : null;
    const canvas = this.map.getCanvas();
    const onScreen = (position: LngLat) => {
      const point = this.map!.project(position);
      return point.x > -VIEW_MARGIN_PX && point.y > -VIEW_MARGIN_PX && point.x < canvas.clientWidth + VIEW_MARGIN_PX && point.y < canvas.clientHeight + VIEW_MARGIN_PX;
    };

    const transform = new THREE.Matrix4()
      .makeTranslation(this.origin.x, this.origin.y, this.origin.z)
      .scale(new THREE.Vector3(this.meterScale, -this.meterScale, this.meterScale))
      .multiply(new THREE.Matrix4().makeRotationX(Math.PI / 2));
    this.camera.projectionMatrix = new THREE.Matrix4()
      .fromArray(options.defaultProjectionData.mainMatrix as unknown as number[])
      .multiply(transform);
    this.hasProjection = true;

    const metersPerPixel = (EARTH_CIRCUMFERENCE_M * Math.cos((this.map.getCenter().lat * Math.PI) / 180)) / (512 * 2 ** zoom);
    const phaseStart = performance.now();
    this.updateScatterDetail(metersPerPixel, zoom);
    const scatterEnd = performance.now();
    const frame: DecorFrame = { elapsed, delta, metersPerPixel, movingAnimated, staticAnimated, onScreen, animating: false, active: 0, animated: 0, dynamicMeshes: [], vatInstances: [] };
    const lodStep = lodStepOf(metersPerPixel);
    if (this.fullPassNeeded) {
      this.fullPassNeeded = false;
      this.activeIds = [];
      this.looseIds = [];
      for (const item of this.items) {
        const kind = this.updateItem(item, frame);
        if (kind === "active") this.activeIds.push(item.id);
        else if (kind === "loose") this.looseIds.push(item.id);
      }
    } else {
      for (const id of this.activeIds) {
        const item = this.itemsById.get(id);
        if (item) this.updateItem(item, frame);
      }
      if (lodStep !== this.lodStep) {
        for (const id of this.looseIds) {
          const item = this.itemsById.get(id);
          if (item) this.updateItem(item, frame);
        }
      }
    }
    this.lodStep = lodStep;
    this.syncMoverBatches(frame.dynamicMeshes);
    this.syncVatBatches(frame.vatInstances);
    this.waterRipples.end();
    let animating = frame.animating || (movingAnimated && waterUniforms.uWaterDetail.value > 0 && this.pits.some((pit) => pit.water));

    const itemsEnd = performance.now();
    this.renderPreview();
    if (this.batchesDirty && shouldSyncBatches({ cameraMoving: moving, now, lastSync: this.lastBatchSync })) this.syncBatches(now);
    animating = this.updateBatchVisibility(metersPerPixel, staticAnimated) || animating;

    const batchesEnd = performance.now();
    this.renderer.resetState();
    this.renderer.info.reset();
    this.bakePendingImpostors();
    this.bakePendingVat();
    this.uploadPendingTexture();
    const openings = this.pitRoots.length > 0 || this.openingInstances > 0;
    // Les matrices sont calculées une fois pour toutes les passes de l'image. `scene.updateMatrixWorld()`
    // descendrait aussi dans les objets immobiles, et leurs squelettes comptent des milliers de nœuds.
    for (const child of this.scene.children) if (child.matrixWorldAutoUpdate) child.updateMatrixWorld();
    this.scene.matrixWorldAutoUpdate = false;
    if (openings) this.renderOpenings();
    this.camera.layers.set(0);
    this.renderer.render(this.scene, this.camera);
    if (openings) this.clearOpeningStencil();
    this.scene.matrixWorldAutoUpdate = true;
    if (this.batchesDirty) this.map.triggerRepaint();
    if (animating) this.scheduleAnimationFrame();
    const end = performance.now();
    this.stats.count(`${PHASE_PREFIX}Préparation`, end, phaseStart - now);
    this.stats.count(`${PHASE_PREFIX}Semis du sol`, end, scatterEnd - phaseStart);
    this.stats.count(`${PHASE_PREFIX}Objets`, end, itemsEnd - scatterEnd);
    this.stats.count(`${PHASE_PREFIX}Lots`, end, batchesEnd - itemsEnd);
    this.stats.count(`${PHASE_PREFIX}Dessin`, end, end - batchesEnd);
    this.lastFrameCounts = { active: frame.active, animated: frame.animated, drawCalls: this.renderer.info.render.calls, triangles: this.renderer.info.render.triangles };
    this.stats.recordFrame(end - now, end);
  }

  /**
   * Replace un objet, le teinte et fait avancer son animation. Rend ce qu'il faudra en faire aux
   * images suivantes : le revoir à chaque image, seulement quand le zoom change de palier, ou jamais
   * tant que rien ne change — il est alors dessiné par son lot.
   */
  private updateItem(item: DecorItem, frame: DecorFrame): "active" | "loose" | "batched" {
    const instance = this.instances.get(item.id);
    if (!instance) return "batched";
    const model = this.options.models[item.model];
    this.applyColors(item, instance);
    this.applyHighlight(item, instance);
    const movable = !!model && isDecorModelMovable(model);
    const pose = decorPose(item, movable ? frame.elapsed : null);
    const heading = pose.moving ? pose.rotationDeg : undefined;
    const template = this.templates.get(instance.modelId);
    const highlighted = this.isHighlighted(item.id);
    const batched = !!template?.instanceable && instance.highlight === "none" && !this.isMover(item);
    let placed = this.placed.get(item.id);
    // Un objet regroupé s'efface par le compte de son lot, sans être revu (`updateBatchVisibility`).
    const hidden = !!pose.hidden || (!batched && !highlighted && this.tooSmallOnScreen(item, instance, frame.metersPerPixel));
    const freshRoot = placed?.root !== instance.root;
    const replaced = pose.moving || placed?.item !== item || placed.root !== instance.root || placed.groundsVersion !== this.groundsVersion || placed.hidden !== hidden || placed.batched !== batched;
    const vat = template?.skinned && instance.highlight === "none" ? this.vatOf(instance.modelId) : null;
    if (replaced) {
      instance.root.visible = !hidden;
      const elevation = this.elevationOf(item, pose.position, heading);
      if (vat) this.placeWithoutSkeleton(instance, pose.position, pose.rotationDeg, item.scale, elevation);
      else this.place(instance.root, pose.position, pose.rotationDeg, item.scale, elevation);
      const chunk = batched ? chunkKeyOf(instance.root.position.x, instance.root.position.z) : null;
      this.moveToChunk(item.id, placed?.chunk ?? null, chunk, placed?.batched || batched);
      placed = { item, root: instance.root, groundsVersion: this.groundsVersion, hidden, floating: isOnWaterSurface(pose.position, elevation, this.pits), batched, chunk, lodMetersPerPixel: this.lodMetersPerPixel(item, instance) };
      this.placed.set(item.id, placed);
    }
    const moverBatched = !batched && !!template?.instanceable && instance.highlight === "none" && this.isMover(item);
    // Hors de la scène, le clone sert encore à la visée ; teinté, il y revient.
    const drawnByBatch = batched || moverBatched || !!vat;
    if (drawnByBatch && instance.root.parent === this.scene) this.scene.remove(instance.root);
    else if (!drawnByBatch && instance.root.parent !== this.scene) this.scene.add(instance.root);
    if (moverBatched && !hidden) frame.dynamicMeshes.push(...instance.meshes);

    const animated = (movable ? frame.movingAnimated : frame.staticAnimated) && (!!instance.mixer || pose.moving);
    const visible = animated && !hidden && frame.onScreen(pose.position);
    const clipName = pose.moving && !(movable && flightAt(pose.position, this.flights, heading)?.flight.escalator) ? "walk" : "idle";
    const timeScale = ((item.speedKmh ?? WALK_SPEED_KMH) / (model?.walkSpeedKmh ?? WALK_SPEED_KMH) / item.scale) * (item.reverse ? -1 : 1);
    if (vat && !hidden) {
      const clip = vat.clips.get(clipName) ?? vat.clips.values().next().value!;
      if (visible) instance.vatTime += frame.delta * timeScale;
      const phase = clip.duration > 0 ? ((instance.vatTime / clip.duration) % 1 + 1) % 1 : 0;
      frame.vatInstances.push({ instance, clip, phase });
      // Barbe, sac à dos… suivent les poses avec le corps ; seul le reste est dessiné à part.
      for (const mesh of instance.meshes) if (!isVatMesh(mesh)) frame.dynamicMeshes.push(mesh);
      if (visible) frame.animated++;
    }
    const clipBefore = instance.current;
    if (instance.mixer && !vat) this.playClip(instance, clipName, timeScale, animated);
    const posed = !!instance.mixer && !vat && (visible || freshRoot || instance.current !== clipBefore);
    if (posed) {
      instance.mixer?.update(visible ? frame.delta : 0);
      frame.animated++;
    }
    // `place` a déjà recalculé les matrices de l'objet : seule une pose nouvelle demande de les refaire.
    instance.root.matrixWorldAutoUpdate = posed;
    if (instance.smoke) {
      instance.root.updateMatrixWorld(true);
      instance.smoke.source.getWorldPosition(this.smokeSource);
      instance.smoke.plume.update(this.smokeSource, item.smoke ?? DEFAULT_DECOR_SMOKE, item.scale, visible ? frame.delta : 0);
      instance.smoke.plume.group.visible &&= !pose.hidden;
    }
    frame.animating ||= visible || (!batched && frame.staticAnimated && !!template?.windy && !hidden && frame.onScreen(pose.position));
    if (placed?.floating && !hidden && template && (instance.mixer || pose.moving)) {
      this.waterRipples.add(item.id, instance.root, template.bounds, pose.moving && frame.movingAnimated);
    }
    frame.active++;
    if (this.isMover(item) || instance.mixer || instance.smoke || highlighted) return "active";
    return batched ? "batched" : "loose";
  }

  /**
   * Les objets en mouvement qui peuvent l'être sont regroupés à part, un lot par maillage : leurs
   * matrices sont réécrites à chaque image, ce qui ne coûte rien pour quelques véhicules, sans jamais
   * toucher aux lots des objets fixes.
   */
  private syncMoverBatches(meshes: THREE.Mesh[]): void {
    const groups = new Map<string, { source: THREE.Mesh; matrices: THREE.Matrix4[] }>();
    for (const mesh of meshes) {
      if (!isShown(mesh)) continue;
      const key = batchKey(mesh);
      let group = groups.get(key);
      if (!group) groups.set(key, (group = { source: mesh, matrices: [] }));
      group.matrices.push(mesh.matrixWorld);
    }
    for (const [key, batch] of this.moverBatches) {
      if (groups.has(key)) continue;
      this.disposeBatch(batch);
      this.moverBatches.delete(key);
    }
    for (const [key, { source, matrices }] of groups) {
      let batch = this.moverBatches.get(key);
      if (batch && batch.capacity < matrices.length) {
        this.disposeBatch(batch);
        batch = undefined;
      }
      if (!batch) {
        batch = this.createBatch(source, Math.max(4, 2 ** Math.ceil(Math.log2(matrices.length))), "movers", { impostorOf: null, impostor: false, windy: false });
        // Ils bougent à chaque image : leur sphère englobante serait toujours à refaire.
        batch.mesh.frustumCulled = false;
        this.moverBatches.set(key, batch);
      }
      const array = batch.mesh.instanceMatrix.array as Float32Array;
      matrices.forEach((matrix, index) => array.set(matrix.elements, index * 16));
      batch.mesh.count = matrices.length;
      batch.mesh.instanceMatrix.needsUpdate = true;
    }
  }

  /** Les poses précalculées du modèle ; le premier appel les met en file, un modèle par image (`bakePendingVat`). */
  private vatOf(templateKey: string): VatBake | null {
    const template = this.templates.get(templateKey);
    if (!template) return null;
    if (template.vat === undefined) {
      template.vat = "pending";
      this.map?.triggerRepaint();
    }
    return template.vat === "pending" ? null : template.vat;
  }

  private bakePendingVat(): void {
    for (const template of this.templates.values()) {
      if (template.vat !== "pending") continue;
      // Un modèle à la fois, quelques millisecondes par image : d'un seul tenant, chacun figeait la carte.
      const deadline = performance.now() + VAT_BAKE_BUDGET_MS;
      template.vatBaker ??= startVatBake(template.root, template.clips);
      const bake = template.vatBaker ? template.vatBaker.step(deadline) : null;
      if (template.vatBaker && !bake) {
        this.map?.triggerRepaint();
        return;
      }
      template.vat = bake;
      template.vatBaker = undefined;
      for (const mesh of bake?.meshes ?? []) this.texturesToUpload.push(mesh.positions, mesh.normals);
      this.fullPassNeeded = true;
      this.map?.triggerRepaint();
      return;
    }
  }

  /** Un lot par maillage animé et par matériau ; chaque instance porte sa matrice et son instant (`vatClip`). */
  private syncVatBatches(entries: { instance: DecorInstance; clip: VatClip; phase: number }[]): void {
    const groups = new Map<string, { mesh: VatMesh; material: THREE.Material; items: { matrix: THREE.Matrix4; clip: VatClip; phase: number }[] }>();
    for (const { instance, clip, phase } of entries) {
      const bake = this.templates.get(instance.modelId)?.vat;
      if (!bake || bake === "pending") continue;
      const animated = instance.meshes.filter(isVatMesh);
      for (const vatMesh of bake.meshes) {
        const mesh = animated[vatMesh.index];
        if (!mesh || !isShown(mesh) || Array.isArray(mesh.material)) continue;
        const key = `${instance.modelId}|${vatMesh.index}|${mesh.material.uuid}`;
        let group = groups.get(key);
        if (!group) groups.set(key, (group = { mesh: vatMesh, material: mesh.material, items: [] }));
        group.items.push({ matrix: instance.root.matrixWorld, clip, phase });
      }
    }
    for (const [key, batch] of this.vatBatches) {
      if (groups.has(key)) continue;
      this.disposeVatBatch(batch);
      this.vatBatches.delete(key);
    }
    for (const [key, group] of groups) {
      let batch = this.vatBatches.get(key);
      if (batch && batch.capacity < group.items.length) {
        this.disposeVatBatch(batch);
        batch = undefined;
      }
      if (!batch) {
        const capacity = Math.max(4, 2 ** Math.ceil(Math.log2(group.items.length)));
        const mesh = new THREE.InstancedMesh(group.mesh.geometry.clone(), vatMaterial(group.material, group.mesh), capacity);
        mesh.geometry.setAttribute("vatClip", new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3));
        mesh.frustumCulled = false;
        mesh.matrixAutoUpdate = false;
        this.scene.add(mesh);
        batch = { mesh, capacity };
        this.vatBatches.set(key, batch);
      }
      const matrices = batch.mesh.instanceMatrix.array as Float32Array;
      const clips = batch.mesh.geometry.getAttribute("vatClip") as THREE.InstancedBufferAttribute;
      const clipArray = clips.array as Float32Array;
      group.items.forEach(({ matrix, clip, phase }, index) => {
        matrices.set(matrix.elements, index * 16);
        clipArray[index * 3] = clip.start;
        clipArray[index * 3 + 1] = clip.frames;
        clipArray[index * 3 + 2] = phase;
      });
      batch.mesh.count = group.items.length;
      batch.mesh.instanceMatrix.needsUpdate = true;
      clips.needsUpdate = true;
    }
  }

  private isMover(item: DecorItem): boolean {
    const model = this.options.models[item.model];
    return !!model && isDecorModelMovable(model) && (item.path?.length ?? 0) >= 2;
  }

  /** Au-delà de ces mètres par pixel, l'objet fait moins de `MIN_DECOR_SCREEN_PX` à l'écran. */
  private lodMetersPerPixel(item: DecorItem, instance: DecorInstance): number {
    const bounds = this.templates.get(instance.modelId)?.bounds;
    if (!bounds) return Infinity;
    return (decorScreenSize(bounds) * item.scale) / MIN_DECOR_SCREEN_PX;
  }

  /** Tient à jour les membres des secteurs : un objet qui en change salit l'ancien et le nouveau. */
  private moveToChunk(id: string, from: string | null, to: string | null, dirty: boolean): void {
    if (from !== to || dirty) {
      if (from !== null) this.dirtyChunks.add(from);
      if (to !== null) this.dirtyChunks.add(to);
      this.batchesDirty ||= from !== null || to !== null;
    }
    if (from === to) return;
    if (from !== null) this.chunkMembers.get(from)?.delete(id);
    if (to !== null) {
      let members = this.chunkMembers.get(to);
      if (!members) this.chunkMembers.set(to, (members = new Set()));
      members.add(id);
    }
  }

  /**
   * Calé sur le rafraîchissement de l'écran, en comptant ses images : un minuteur tomberait tantôt
   * avant, tantôt après l'une d'elles, et le mouvement saccaderait à cadence pourtant égale. MapLibre
   * ne dessine qu'à l'image qui suit sa demande : elle part donc une image plus tôt.
   */
  private scheduleAnimationFrame(): void {
    if (this.animationRequest !== null) return;
    if (this.screenFrameMs !== null && Math.round(ANIMATION_FRAME_MS / this.screenFrameMs) <= 1) {
      this.map?.triggerRepaint();
      return;
    }
    let previous: number | null = null;
    let skipped = 0;
    const wait = (time: number) => {
      if (previous !== null) this.screenFrameMs = time - previous;
      previous = time;
      skipped++;
      const frame = this.screenFrameMs;
      if (frame === null || skipped < Math.max(1, Math.round(ANIMATION_FRAME_MS / frame)) - 1) {
        this.animationRequest = requestAnimationFrame(wait);
        return;
      }
      this.animationRequest = null;
      this.map?.triggerRepaint();
    };
    this.animationRequest = requestAnimationFrame(wait);
  }

  private clearOpeningStencil(): void {
    const gl = this.renderer!.getContext();
    gl.stencilMask(OPENING_STENCIL_BIT);
    gl.clearStencil(0);
    gl.clear(gl.STENCIL_BUFFER_BIT);
    this.renderer!.resetState();
  }

  /**
   * La plage de profondeur que MapLibre a fixée pour ce calque, lue dans l'état qu'il tient à jour :
   * la demander à WebGL (`getParameter`) attend que le GPU ait vidé sa file, plusieurs millisecondes
   * en pleine image. Repli sur WebGL si cet état interne venait à changer de forme.
   */
  private currentDepthRange(gl: WebGLRenderingContext | WebGL2RenderingContext): ArrayLike<number> {
    const cached = (this.map as unknown as { painter?: { context?: { depthRange?: { current?: unknown } } } } | null)?.painter?.context?.depthRange?.current;
    if (Array.isArray(cached) && cached.length === 2 && cached.every((value) => typeof value === "number")) return cached as number[];
    return gl.getParameter(gl.DEPTH_RANGE) as Float32Array;
  }

  private renderOpenings(): void {
    const renderer = this.renderer!;
    const gl = renderer.getContext();
    const range = this.currentDepthRange(gl);
    const pass = (layer: number) => {
      this.camera.layers.set(layer);
      renderer.render(this.scene, this.camera);
    };

    // Chaque passe parcourt toute la scène ; un objet masqué est écarté sans qu'elle descende dedans.
    const idle = this.scene.children.filter((child) => child.visible && !this.takesPartInOpenings(child));
    for (const child of idle) child.visible = false;
    this.clearOpeningStencil();
    pass(LAYER_MASK);
    pass(LAYER_MASK_OCCLUSION);
    gl.depthRange(1, 1);
    pass(LAYER_DEPTH_RESET);
    gl.depthRange(range[0]!, range[1]!);
    pass(LAYER_PIT);
    const center = this.map!.getCenter();
    this.place(this.groundSeal, [center.lng, center.lat], 0, 1);
    pass(LAYER_GROUND_SEAL);
    for (const child of idle) child.visible = true;
  }

  private updateScatterDetail(metersPerPixel: number, zoom: number): void {
    if (!this.surfaceScatter.hasContent || !this.map) return;
    const [x, z] = this.toLocal([this.map.getCenter().lng, this.map.getCenter().lat]);
    const depthOf = (point: THREE.Vector3) => new THREE.Vector4(point.x, point.y, point.z, 1).applyMatrix4(this.camera.projectionMatrix).w;
    const centerDepth = depthOf(new THREE.Vector3(x, 0, z));
    this.surfaceScatter.updateDetail({ centerDepth, metersPerPixel, zoom, time: this.waterClock }, (point) => {
      const depth = depthOf(point);
      return depth > 0 && centerDepth > 0 ? centerDepth / depth / metersPerPixel : 0;
    });
  }

  private takesPartInOpenings(object: THREE.Object3D): boolean {
    let takesPart = this.openingParticipants.get(object);
    if (takesPart === undefined) {
      takesPart = false;
      object.traverse((node) => {
        takesPart ||= node instanceof THREE.Light || (node.layers.mask & ~1) !== 0;
      });
      this.openingParticipants.set(object, takesPart);
    }
    return takesPart;
  }

  private renderPreview(): void {
    const item = this.preview;
    if (!item) {
      if (this.previewInstance) this.previewInstance.root.visible = false;
      return;
    }
    const key = this.templateKey(item);
    const template = this.templates.get(key);
    if (!template) return;
    if (!this.previewInstance) {
      this.previewInstance = this.instantiate(key, template);
      this.previewInstance.root.traverse((node) => {
        if (!(node instanceof THREE.Mesh)) return;
        const materials = (Array.isArray(node.material) ? node.material : [node.material]).map((material: THREE.Material) => {
          const copy = withShader(material.clone(), material);
          copy.transparent = true;
          copy.opacity = PREVIEW_OPACITY;
          copy.depthWrite = false;
          return copy;
        });
        node.material = materials.length === 1 ? materials[0]! : materials;
      });
      if (this.previewInstance.mixer) {
        this.playClip(this.previewInstance, "idle", 1, false);
        this.previewInstance.mixer.update(0);
      }
    }
    this.previewInstance.root.visible = true;
    this.place(this.previewInstance.root, item.position, item.rotationDeg, item.scale, this.elevationOf(item, item.position));
  }

  private ensureOrigin(): void {
    if (this.origin) return;
    const anchor = this.items[0]?.position ?? this.preview?.position ?? this.pits[0]?.ring[0] ?? this.railings[0]?.lines[0]?.[0] ?? this.scatterAreas[0]?.ring[0] ?? this.roofs[0]?.ring[0];
    if (!anchor) return;
    this.origin = maplibregl.MercatorCoordinate.fromLngLat(anchor, 0);
    this.meterScale = this.origin.meterInMercatorCoordinateUnits();
  }

  private place(object: THREE.Object3D, position: LngLat, rotationDeg: number, scale: number, elevation = 0): void {
    if (!this.origin) return;
    this.setPose(object, position, rotationDeg, scale, elevation);
    refreshWorldMatrix(object);
  }

  private setPose(object: THREE.Object3D, position: LngLat, rotationDeg: number, scale: number, elevation: number): void {
    const point = maplibregl.MercatorCoordinate.fromLngLat(position, 0);
    object.position.set((point.x - this.origin!.x) / this.meterScale, elevation, (point.y - this.origin!.y) / this.meterScale);
    object.rotation.y = (-rotationDeg * Math.PI) / 180;
    object.scale.setScalar(scale);
  }

  /**
   * Un objet dessiné par poses précalculées n'a besoin que de sa propre matrice, et de celles de ses
   * maillages fixes (l'ombre) : refaire tout son squelette à chaque image ne servait à rien.
   */
  private placeWithoutSkeleton(instance: DecorInstance, position: LngLat, rotationDeg: number, scale: number, elevation: number): void {
    if (!this.origin) return;
    const root = instance.root;
    this.setPose(root, position, rotationDeg, scale, elevation);
    root.updateMatrix();
    root.matrixWorld.copy(root.matrix);
    for (const mesh of instance.meshes) if (!isVatMesh(mesh)) worldMatrixFromRoot(mesh, root);
  }

  private syncInstances(): void {
    this.syncingInstances = true;
    try {
      this.syncAllInstances();
    } finally {
      this.syncingInstances = false;
    }
  }

  private syncAllInstances(): void {
    this.fullPassNeeded = true;
    const alive = new Set<string>();
    for (const item of this.items) {
      alive.add(item.id);
      const key = this.templateKey(item);
      const existing = this.instances.get(item.id);
      if (existing && existing.modelId === key) continue;
      if (existing) {
        this.disposeInstance(existing);
        this.instances.delete(item.id);
      }
      // Un modèle procédural se construit sur-le-champ : l'objet est créé dans la même passe.
      if (!this.templates.has(key)) this.loadModel(item);
      const template = this.templates.get(key);
      if (template) this.instances.set(item.id, this.instantiate(key, template));
    }
    for (const [id, instance] of this.instances) {
      if (alive.has(id)) continue;
      this.disposeInstance(instance);
      this.instances.delete(id);
    }
    this.openingInstances = [...this.instances.values()].filter((instance) => this.templates.get(instance.modelId)?.opening).length;
  }

  /**
   * Les poses d'une foule vont au GPU une texture par image, dès leur précalcul : envoyées au premier
   * affichage, plusieurs d'un coup, elles figeaient le premier zoom avant d'une centaine de millisecondes.
   */
  private readonly texturesToUpload: THREE.Texture[] = [];

  private uploadPendingTexture(): void {
    const texture = this.texturesToUpload.shift();
    if (!texture || !this.renderer) return;
    this.renderer.initTexture(texture);
    if (this.texturesToUpload.length) this.map?.triggerRepaint();
  }

  /** Vrai pendant `syncInstances` : un modèle construit à sa demande ne la relance pas. */
  private syncingInstances = false;

  private loadModel(item: DecorItem): void {
    const modelId = item.model;
    const key = this.templateKey(item);
    if (this.templates.has(key) || this.loading.has(key)) return;
    const model = this.options.models[modelId];
    if (!model) return;
    if (model.procedural) {
      const [width, length] = proceduralSize(model, item);
      const built = model.procedural === "tree-sakura"
        ? buildSakura()
        : model.procedural === "tree-pine-japanese"
        ? buildJapanesePine()
        : model.procedural === "tree-olive"
        ? buildOlive()
        : model.procedural === "tree-olive-potted"
        ? buildPottedOlive()
        : model.procedural === "lantern-garland"
        ? buildLanternGarland()
        : model.procedural === "yatai"
        ? buildYatai()
        : model.procedural === "info-desk"
        ? buildInfoDesk()
        : model.procedural === "first-aid-post"
        ? buildFirstAidPost()
        : model.procedural === "castle"
        ? buildFairytaleCastle()
        : model.procedural === "train-locomotive"
        ? buildSteamLocomotive()
        : model.procedural === "train-tender"
        ? buildSteamTender()
        : model.procedural === "train-coach"
        ? buildExcursionCoach()
        : model.procedural === "stage"
        ? buildStage(width, length, { style: model.stageStyle, image: item.image && (this.options.resolveImageUrl?.(item.image) ?? item.image), onImageLoaded: () => this.map?.triggerRepaint() })
        : model.procedural === "seating"
        ? buildSeating(width, length)
        : model.procedural === "picnic"
        ? buildPicnicTables(width, length)
        : model.procedural === "fence"
        ? buildFence(width, model.fenceStyle ?? "wood")
        : model.procedural === "rock-massif"
        ? buildRockMassif(width, length, massifHeight(model, item), model.massifStyle ?? "peak")
        : isFreeFlight(model)
        ? buildFreeFlight(model, width, flightRise(model, item), flightSlope(item))
        : buildStairsOpening(width, length, { depth: item.depth ? roundTenth(item.depth) : undefined, openBottom: item.openBottom });
      const { root, bounds } = normalizeTemplate(built, model);
      const windy = !!model.procedural?.startsWith("tree-") && prepareWind(root, bounds.height);
      const opening = prepareOpening(root);
      // Une rangée de chaises ou une scène est un seul objet, très étendu : une tache à sa taille couvrirait toute la zone.
      if (!opening && isFixedProceduralModel(model)) addContactShadow(root, bounds, this.contactShadowMaterial);
      const instanceable = isInstanceable(root, built.animations, false, opening);
      const colorSlots = opening ? [] : collectColorSlots(root, undefined, (node) => node.name === CONTACT_SHADOW_NODE);
      this.templates.set(key, { root, clips: built.animations, skinned: false, bounds, opening, instanceable, windy, colorSlots });
      if (!this.syncingInstances) this.syncInstances();
      if (opening && this.pits.length) this.buildPits();
      this.map?.triggerRepaint();
      return;
    }
    this.loading.add(modelId);
    this.loader
      .loadAsync(model.url)
      .then((gltf) => {
        const { root, bounds } = normalizeTemplate(gltf.scene, model);
        if (model.coat) prepareCoat(root, model.coat);
        const windy = model.category === "plant" && prepareWind(root, bounds.height);
        const opening = prepareOpening(root);
        if (!opening && !model.shadowless) addContactShadow(root, bounds, this.contactShadowMaterial);
        let skinned = false;
        root.traverse((node) => {
          if (!(node instanceof THREE.SkinnedMesh)) return;
          skinned = true;
          // Calculée à la première image où il est visible, sommet par sommet, et pour chaque copie :
          // une centaine de passants qui apparaissent ensemble en plein zoom figent l'image.
          node.computeBoundingSphere();
        });
        const instanceable = isInstanceable(root, gltf.animations, skinned, opening);
        const colorSlots = opening ? [] : collectColorSlots(root, model.coat ? coatColors(model.coat) : undefined, (node) => node.name === CONTACT_SHADOW_NODE, model.partNames);
        this.templates.set(modelId, { root, clips: gltf.animations, skinned, bounds, opening, instanceable, windy, colorSlots });
        this.syncInstances();
        if (opening && this.pits.length) this.buildPits();
        this.map?.triggerRepaint();
        this.options.onModelLoaded();
      })
      .catch((error: unknown) => console.warn(`[tokamap] Modèle de décor "${modelId}" introuvable`, error))
      .finally(() => this.loading.delete(modelId));
  }

  private instantiate(modelId: string, template: DecorTemplate): DecorInstance {
    const root = template.skinned ? cloneSkinned(template.root) : template.root.clone(true);
    const source = root.getObjectByName(SMOKE_SOURCE_NODE);
    const smoke = source ? { source, plume: new SmokePlume() } : null;
    const instance: DecorInstance = { modelId, root, meshes: meshesOf(root), mixer: null, actions: new Map(), current: null, highlight: "none", smoke, colors: "", recoloredKeys: [], vatTime: Math.random() * 10 };
    if (smoke) this.scene.add(smoke.plume.group);
    if (template.clips.length > 0) {
      instance.mixer = new THREE.AnimationMixer(root);
      for (const clip of template.clips) instance.actions.set(clip.name, instance.mixer.clipAction(clip));
    }
    this.scene.add(root);
    return instance;
  }

  private tooSmallOnScreen(item: DecorItem, instance: DecorInstance, metersPerPixel: number): boolean {
    return this.lodMetersPerPixel(item, instance) < metersPerPixel;
  }

  private isHighlighted(id: string): boolean {
    return this.highlight.selectedId === id || this.highlight.hoveredId === id;
  }

  private playClip(instance: DecorInstance, name: string, timeScale: number, blend: boolean): void {
    const next = instance.actions.get(name) ?? instance.actions.get("idle");
    if (!next) return;
    next.timeScale = timeScale;
    if (instance.current === next) return;
    next.reset().play();
    if (blend && instance.current) {
      next.fadeIn(0.2);
      instance.current.fadeOut(0.2);
    } else {
      instance.current?.stop();
    }
    instance.current = next;
  }

  /**
   * Recompose les lots des secteurs modifiés, dans le budget d'une image : une carte entière à
   * regrouper au chargement s'étale sur quelques images au lieu d'en figer une.
   */
  private syncBatches(now: number): void {
    this.lastBatchSync = now;
    const started = performance.now();
    for (const chunk of [...this.dirtyChunks]) {
      this.dirtyChunks.delete(chunk);
      this.rebuildChunk(chunk);
      if (performance.now() - started > BATCH_SYNC_BUDGET_MS) break;
    }
    this.batchesDirty = this.dirtyChunks.size > 0;
    if (!this.batchesDirty) this.disposeUnusedRecolored();
    const end = performance.now();
    this.stats.count("batchSyncs", end);
    this.stats.count("batchSyncMs", end, end - started);
  }

  private rebuildChunk(chunk: string): void {
    const groups = new Map<string, { source: THREE.Mesh; entries: { matrix: THREE.Matrix4; threshold: number }[]; impostorOf: string | null; impostor: boolean; windy: boolean }>();
    const push = (key: string, source: THREE.Mesh, matrix: THREE.Matrix4, threshold: number, impostorOf: string | null, impostor: boolean, windy: boolean) => {
      let group = groups.get(key);
      if (!group) groups.set(key, (group = { source, entries: [], impostorOf, impostor, windy }));
      group.entries.push({ matrix, threshold });
    };
    for (const id of this.chunkMembers.get(chunk) ?? []) {
      const instance = this.instances.get(id);
      const placed = this.placed.get(id);
      if (!instance || !placed?.batched) continue;
      const template = this.templates.get(instance.modelId);
      if (template && this.wantsImpostor(instance.modelId) && !template.impostor) template.impostor = "pending";
      // Tant que sa version simplifiée n'est pas prête, l'arbre reste dessiné en entier à toute distance.
      const impostorOf = template?.impostor instanceof THREE.Mesh ? instance.modelId : null;
      for (const mesh of instance.meshes) {
        if (!isShown(mesh)) continue;
        push(`${chunk}|${batchKey(mesh)}`, mesh, mesh.matrixWorld, placed.lodMetersPerPixel, impostorOf, false, !!template?.windy);
      }
      if (impostorOf && template?.impostor instanceof THREE.Mesh) {
        push(`${chunk}|impostor|${impostorOf}`, template.impostor, instance.root.matrixWorld, placed.lodMetersPerPixel, impostorOf, true, false);
      }
    }

    for (const [key, batch] of this.batches) {
      if (batch.chunk !== chunk || groups.has(key)) continue;
      this.disposeBatch(batch);
      this.batches.delete(key);
    }
    for (const [key, group] of groups) {
      let batch = this.batches.get(key);
      if (batch && batch.capacity < group.entries.length) {
        this.disposeBatch(batch);
        batch = undefined;
      }
      if (!batch) {
        batch = this.createBatch(group.source, Math.max(16, 2 ** Math.ceil(Math.log2(group.entries.length))), chunk, group);
        this.batches.set(key, batch);
      }
      group.entries.sort((a, b) => b.threshold - a.threshold);
      const array = batch.mesh.instanceMatrix.array as Float32Array;
      group.entries.forEach(({ matrix }, index) => array.set(matrix.elements, index * 16));
      batch.thresholds = Float32Array.from(group.entries, (entry) => entry.threshold);
      batch.mesh.count = group.entries.length;
      batch.mesh.instanceMatrix.needsUpdate = true;
      batch.mesh.computeBoundingSphere();
    }
  }

  /** Les arbres, et eux seuls : leur feuillage pèse lourd et se lit très bien en simple image de loin. */
  private wantsImpostor(templateKey: string): boolean {
    const template = this.templates.get(templateKey);
    const model = this.options.models[templateKey];
    return !!template && !template.opening && !template.skinned && model?.category === "plant";
  }

  /**
   * Fixe, secteur par secteur, combien d'instances de chaque lot dessiner et si un arbre l'est en
   * entier ou par sa version simplifiée — selon la taille à l'écran, plus petite au loin quand la vue
   * est inclinée. Rend vrai s'il faut redessiner pour le vent.
   */
  private updateBatchVisibility(metersPerPixel: number, staticAnimated: boolean): boolean {
    if (!this.map) return false;
    const [x, z] = this.toLocal([this.map.getCenter().lng, this.map.getCenter().lat]);
    const depthOf = (px: number, pz: number) => new THREE.Vector4(px, 0, pz, 1).applyMatrix4(this.camera.projectionMatrix).w;
    const centerDepth = depthOf(x, z);
    const chunkScale = new Map<string, number>();
    const scaleOf = (chunk: string) => {
      let scale = chunkScale.get(chunk);
      if (scale === undefined) {
        const [cx, cz] = chunkCenter(chunk);
        const depth = depthOf(cx, cz);
        // Un secteur derrière la caméra n'a pas de taille à l'écran : son plus proche bord peut pourtant l'être.
        scale = depth > 0 && centerDepth > 0 ? Math.max(1, depth / centerDepth) : 1;
        chunkScale.set(chunk, scale);
      }
      return scale;
    };
    let windyVisible = false;
    let visibleInstances = 0;
    let impostors = 0;
    for (const batch of this.batches.values()) {
      const chunkMetersPerPixel = metersPerPixel * scaleOf(batch.chunk);
      const far = chunkMetersPerPixel >= IMPOSTOR_METERS_PER_PIXEL;
      const shown = batch.impostorOf === null || batch.impostor === far;
      const count = shown ? visibleInstanceCount(batch.thresholds, chunkMetersPerPixel) : 0;
      batch.mesh.count = count;
      batch.mesh.visible = count > 0;
      visibleInstances += count;
      if (batch.impostor) impostors += count;
      windyVisible ||= batch.windy && count > 0;
    }
    this.visibleCounts = { visibleInstances, impostors };
    return staticAnimated && windyVisible;
  }

  /** Version simplifiée d'un arbre : son image de face, posée sur deux plans croisés, éclairée une fois pour toutes. */
  private bakePendingImpostors(): void {
    if (!this.renderer) return;
    let baked = false;
    for (const [key, template] of this.templates) {
      if (template.impostor !== "pending") continue;
      template.impostor = bakeImpostor(this.renderer, template, [this.skyLight, this.sunLight]);
      baked = true;
      for (const [chunk, members] of this.chunkMembers) {
        if ([...members].some((id) => this.instances.get(id)?.modelId === key)) this.dirtyChunks.add(chunk);
      }
    }
    if (baked) {
      this.batchesDirty = true;
      this.renderer.resetState();
    }
  }

  getStats(): DecorRenderStats {
    const now = performance.now();
    const snapshot = this.stats.snapshot(now);
    let batchedInstances = 0;
    for (const batch of this.batches.values()) if (!batch.impostor) batchedInstances += batch.thresholds.length;
    return {
      timings: snapshot.timings,
      items: this.items.length,
      active: this.lastFrameCounts.active,
      standalone: [...this.instances.values()].filter((instance) => instance.root.parent === this.scene).length,
      batches: this.batches.size,
      batchedInstances,
      visibleInstances: this.visibleCounts.visibleInstances,
      impostors: this.visibleCounts.impostors,
      scattered: this.surfaceScatter.drawn,
      animated: this.lastFrameCounts.animated,
      drawCalls: this.lastFrameCounts.drawCalls,
      triangles: this.lastFrameCounts.triangles,
      batchSyncsPerSecond: snapshot.counts.batchSyncs ?? 0,
      batchSyncMs: snapshot.counts.batchSyncMs ?? 0,
      phases: Object.fromEntries(Object.entries(snapshot.counts)
        .filter(([name]) => name.startsWith(PHASE_PREFIX))
        .map(([name, total]) => [name.slice(PHASE_PREFIX.length), snapshot.timings.count ? total / snapshot.timings.count : 0])),
    };
  }

  private createBatch(source: THREE.Mesh, capacity: number, chunk: string, group: { impostorOf: string | null; impostor: boolean; windy: boolean }): DecorBatch {
    const mesh = new THREE.InstancedMesh(source.geometry, source.material, capacity);
    mesh.count = 0;
    mesh.layers.mask = source.layers.mask;
    mesh.renderOrder = source.renderOrder;
    mesh.matrixAutoUpdate = false;
    this.scene.add(mesh);
    return { mesh, capacity, chunk, thresholds: new Float32Array(0), impostorOf: group.impostorOf, impostor: group.impostor, windy: group.windy };
  }

  private disposeBatch(batch: DecorBatch): void {
    this.scene.remove(batch.mesh);
    batch.mesh.dispose();
  }

  /** Sa géométrie est une copie propre au lot : `InstancedMesh.dispose` ne la libère pas. */
  private disposeVatBatch(batch: { mesh: THREE.InstancedMesh }): void {
    this.scene.remove(batch.mesh);
    batch.mesh.geometry.dispose();
    batch.mesh.dispose();
  }

  private disposeInstance(instance: DecorInstance): void {
    this.releaseRecolored(instance);
    instance.mixer?.stopAllAction();
    this.scene.remove(instance.root);
    if (instance.smoke) {
      this.scene.remove(instance.smoke.plume.group);
      instance.smoke.plume.dispose();
    }
  }
}
